/* WAV Player - Sample Editor. Built 2026-10-03 03:55 by web_studio/build.py from web_studio/src/. Do not hand-edit: change the sources and rebuild. */
(function () {
'use strict';
// ---- rules.js --------------------------------------------------------------
// WAV Player - Sample Editor: the firmware's WAV player rules, in one place.
//
// Every number the page shows comes from here. Each constant names the
// firmware line it mirrors (VModPro/main.cpp unless noted) so that when the
// firmware changes, this file is the only thing that has to follow it.
const Rules = (() => {
  // ---- Memory -------------------------------------------------------------
  // WAV_SDRAM_SIZE: one 32 MiB pool shared by every enabled sample in the
  // active preset. It is not a size limit any more: since the SD work was
  // promoted to production, loadSampleToSdram() sends a file that does not
  // fit to wavStreamAttach() and it plays from the card instead. The pool is
  // now the line between "held in memory" and "streamed".
  const POOL_BYTES = 32 * 1024 * 1024;
  // WAV_MAX_SAMPLES: sample slots per preset.
  const MAX_SLOTS = 16;
  // WAV_MAX_STREAMS: streamed voices at once. A third oversized file that is
  // triggered while two are streaming gets STREAM_FULL.
  const MAX_STREAMS = 2;
  // scanWavFiles(): "if (wavScanCount >= 32) break". File 33 in the folder
  // is never listed, in whatever order the FAT directory happens to hold.
  const MAX_FILES = 32;

  // ---- Names --------------------------------------------------------------
  // SampleConfig::filename[32] and populateWavFiles() strncpy 31: a longer
  // name is truncated in the browser list, and the truncated name is what
  // gets opened, so the open fails ("Can't open file").
  const NAME_MAX = 31;
  // scanWavFiles() accepts exactly ".wav" or ".WAV" (strcmp on the extension).
  const EXT = '.wav';
  const BASE_MAX = NAME_MAX - EXT.length;   // 27
  // Sample settings page: strncpy(headerBuf, fname, 19). Longer names are cut
  // on that one screen. Advisory, not a failure.
  const BASE_SOFT = 19;
  // libDaisy ffconf.h: _LFN_UNICODE 0 with _CODE_PAGE 850. Anything outside
  // that code page is mangled on the way through the filesystem API, so the
  // page only ever writes ASCII letters, digits, underscore and hyphen.
  const SAFE_NAME = /^[A-Za-z0-9_-]+$/;
  // Windows refuses to create these as file names regardless of extension.
  // FatFS does not care, but the card is written from a computer.
  const RESERVED = /^(CON|PRN|AUX|NUL|COM[1-9]|LPT[1-9])$/i;
  // Folder on the card. Created by the device's own Format.
  const FOLDER = '/VMOD/samples/';

  // ---- Format -------------------------------------------------------------
  const BITS = 16;                          // BAD_BITS: bitsPerSample != 16
  const RATE_MIN = 8000, RATE_MAX = 96000;  // BAD_RATE
  // slot.rateRatio = wavRate / 48000: anything else is linearly interpolated
  // at playback. 48 kHz plays untouched.
  const DEVICE_RATE = 48000;
  // 24 kHz halves the memory cost for long beds at a small quality cost.
  const OUTPUT_RATES = [48000, 24000];
  // Stereo is summed to mono in the audio callback ((l + r) * 0.5), so the
  // page never writes stereo: it would double the memory for nothing.
  const CHANNELS = 1;
  const BYTES_PER_FRAME = CHANNELS * (BITS / 8);

  // Refuse decoding inputs longer than this. 30 minutes of float at 48 kHz
  // is 345 MB in a tab for a file that can never fit on the device.
  const MAX_INPUT_SECONDS = 30 * 60;
  // Warn when one export eats half the preset budget by itself.
  const WARN_BYTES = POOL_BYTES / 2;

  // The exact strings wavLoadErrorText() puts on the OLED, so the page can
  // say "the device would show:" and be right.
  const VERDICT = {
    OK: '',
    OPEN_FAILED: "Can't open file",
    BAD_HEADER: 'Bad WAV header',
    BAD_BITS: 'Need 16-bit WAV',
    NOT_PCM: 'Not PCM audio',
    BAD_CHANNELS: 'Need mono or stereo',
    BAD_RATE: 'Rate must be 8-96kHz',
    EMPTY_DATA: 'Empty audio data',
    READ_FAILED: 'SD read failed',
    SLOTS_FULL: 'Sample slots full',
    // Streaming refusals. Neither is about the file itself, so nothing the
    // page exports can avoid them; they depend on the card and on how many
    // oversized samples are already playing.
    STREAM_FULL: `Stream slots full (${MAX_STREAMS})`,
    SD_TOO_SLOW: 'SD too slow to stream',
  };

  // ---- Helpers ------------------------------------------------------------

  // Turn any user or source file name into a base name the device can open.
  // Strips every extension, maps unsafe characters to '_', collapses runs,
  // trims the ends, caps the length. Never returns an empty string.
  function sanitizeBase(raw) {
    let s = String(raw || '');
    s = s.replace(/\.[A-Za-z0-9]{1,5}$/, '');     // "take 1.final.wav" -> "take 1.final"
    s = s.normalize('NFKD').replace(/[\u0300-\u036f]/g, ''); // strip combining accents first
    s = s.replace(/[^A-Za-z0-9_-]+/g, '_');
    s = s.replace(/_+/g, '_').replace(/^[_-]+|[_-]+$/g, '');
    if (RESERVED.test(s)) s += '_';
    if (s.length > BASE_MAX) s = s.slice(0, BASE_MAX).replace(/[_-]+$/, '');
    return s || 'SAMPLE';
  }

  function fileName(base) { return base + EXT; }

  // Classify a base name the user typed. level: ok | warn | bad.
  function nameStatus(base) {
    const s = String(base || '');
    if (!s.length) return { level: 'bad', message: 'Enter a name.' };
    if (!SAFE_NAME.test(s))
      return { level: 'bad', message: 'Letters, digits, underscore and hyphen only.' };
    if (RESERVED.test(s))
      return { level: 'bad', message: 'Windows will not create a file with that name.' };
    if (s.length > BASE_MAX)
      return { level: 'bad', message: `Too long: ${s.length} of ${BASE_MAX}. The device could not open it.` };
    if (s.length > BASE_SOFT)
      return { level: 'warn', message: `Shows as "${s.slice(0, BASE_SOFT)}" on the sample settings screen.` };
    return { level: 'ok', message: `Device lists it as ${fileName(s)}` };
  }

  // Is this a file the device's folder scan would even list?
  function isListable(name) {
    return /\.(wav|WAV)$/.test(name) && name.length <= NAME_MAX;
  }

  const bytesForFrames = (frames) => frames * BYTES_PER_FRAME;
  const framesForSeconds = (sec, rate) => Math.round(sec * rate);
  const secondsForBytes = (bytes, rate) => bytes / BYTES_PER_FRAME / rate;

  // Pool accounting for a list of data-chunk byte counts.
  function budget(byteCounts) {
    const used = byteCounts.reduce((a, b) => a + b, 0);
    return {
      used, pool: POOL_BYTES,
      free: Math.max(0, POOL_BYTES - used),
      pct: Math.min(100, (used / POOL_BYTES) * 100),
      over: used > POOL_BYTES,
      secondsAt48k: secondsForBytes(used, DEVICE_RATE),
    };
  }

  // Plain words for what the device will do with a file of this size: hold
  // it in memory, or play it from the card.
  function playbackMode(bytes) {
    return bytes > POOL_BYTES ? 'streamed' : 'resident';
  }

  function formatBytes(b) {
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
    return `${(b / (1024 * 1024)).toFixed(b < 10 * 1024 * 1024 ? 2 : 1)} MB`;
  }

  function formatDuration(sec, withMs) {
    if (!isFinite(sec) || sec < 0) sec = 0;
    const m = Math.floor(sec / 60), s = sec - m * 60;
    if (withMs) return `${m}:${s.toFixed(2).padStart(5, '0')}`;
    return `${m}:${Math.floor(s).toString().padStart(2, '0')}`;
  }

  // "5:49" in the spec strip: whole seconds of the pool at the device rate.
  const POOL_SECONDS_48K = secondsForBytes(POOL_BYTES, DEVICE_RATE);

  return {
    POOL_BYTES, MAX_SLOTS, MAX_FILES, NAME_MAX, EXT, BASE_MAX, BASE_SOFT,
    SAFE_NAME, FOLDER, BITS, RATE_MIN, RATE_MAX, DEVICE_RATE, OUTPUT_RATES,
    CHANNELS, BYTES_PER_FRAME, MAX_INPUT_SECONDS, WARN_BYTES, VERDICT, MAX_STREAMS,
    POOL_SECONDS_48K,
    sanitizeBase, fileName, nameStatus, isListable, bytesForFrames,
    framesForSeconds, secondsForBytes, budget, playbackMode, formatBytes,
    formatDuration,
  };
})();

// ---- dsp.js ----------------------------------------------------------------
// WAV Player - Sample Editor: pure sample math. No DOM, no Web Audio, so every
// function here runs unchanged under Node for the tests.
const Dsp = (() => {
  // Average all channels into one. For stereo this is (l + r) * 0.5, the
  // exact formula the firmware's audio callback uses, so the mono preview
  // on the page is what the device plays.
  function downmix(channels) {
    if (channels.length === 1) return channels[0];
    const n = channels[0].length, k = 1 / channels.length;
    const out = new Float32Array(n);
    for (const ch of channels) for (let i = 0; i < n; i++) out[i] += ch[i] * k;
    return out;
  }

  const peak = (buf) => {
    let p = 0;
    for (let i = 0; i < buf.length; i++) { const a = Math.abs(buf[i]); if (a > p) p = a; }
    return p;
  };
  const dbToGain = (db) => Math.pow(10, db / 20);
  const gainToDb = (g) => (g > 0 ? 20 * Math.log10(g) : -Infinity);

  function applyGain(buf, g) {
    if (g === 1) return buf;
    for (let i = 0; i < buf.length; i++) buf[i] *= g;
    return buf;
  }

  // Scale so the peak lands on targetDb. Returns the gain applied. Silence
  // is left alone (nothing to normalize, and a huge gain would only raise
  // the noise floor).
  function normalize(buf, targetDb) {
    const p = peak(buf);
    if (p < 1e-6) return 1;
    const g = dbToGain(targetDb) / p;
    applyGain(buf, g);
    return g;
  }

  // Raised-cosine fades, in place. Smoother than linear at both ends, which
  // matters because the device loops with a hard jump and any residual
  // level at the seam is heard as a click.
  function fade(buf, inFrames, outFrames) {
    const n = buf.length;
    inFrames = Math.min(Math.max(0, inFrames | 0), n);
    outFrames = Math.min(Math.max(0, outFrames | 0), n);
    for (let i = 0; i < inFrames; i++)
      buf[i] *= 0.5 - 0.5 * Math.cos(Math.PI * i / inFrames);
    for (let i = 0; i < outFrames; i++)
      buf[n - 1 - i] *= 0.5 - 0.5 * Math.cos(Math.PI * i / outFrames);
    return buf;
  }

  // Float [-1, 1] to int16 with TPDF dither (two uniform draws, so the
  // error is decorrelated from the signal) and hard clipping. The dither
  // is one LSB peak to peak, inaudible, and it turns low-level distortion
  // into a flat noise floor on quiet tails.
  function toInt16(buf, dither = true, rand = Math.random) {
    const out = new Int16Array(buf.length);
    for (let i = 0; i < buf.length; i++) {
      let v = buf[i] * 32767;
      if (dither) v += rand() - rand();
      v = Math.round(v);
      out[i] = v > 32767 ? 32767 : v < -32768 ? -32768 : v;
    }
    return out;
  }

  function fromInt16(i16) {
    const out = new Float32Array(i16.length);
    for (let i = 0; i < i16.length; i++) out[i] = i16[i] / 32768;
    return out;
  }

  // Sum placed clips into one buffer. Each clip: { buf, at (frame offset in
  // the mix), gain }. The buffer is already trimmed and faded by the caller.
  function mix(clips, totalFrames) {
    const out = new Float32Array(totalFrames);
    for (const c of clips) {
      const g = c.gain === undefined ? 1 : c.gain;
      const start = Math.max(0, c.at | 0);
      const n = Math.min(c.buf.length, totalFrames - start);
      for (let i = 0; i < n; i++) out[start + i] += c.buf[i] * g;
    }
    return out;
  }

  // Count samples that would clip (|x| >= 1) so the arranger can show it.
  function clippedCount(buf) {
    let c = 0;
    for (let i = 0; i < buf.length; i++) if (buf[i] >= 1 || buf[i] <= -1) c++;
    return c;
  }

  // Min, max and sum of squares at several bucket sizes so a waveform can
  // be drawn at any zoom without touching every sample per frame. Level k
  // has buckets of BASE * 4^k frames. Drawing picks the largest bucket that
  // still gives at least one bucket per pixel and aggregates the rest. The
  // sum of squares gives the RMS body that DAWs draw inside the peak
  // envelope, which is what makes speech look like speech at a glance.
  const MIP_BASE = 256;
  function peaksMipmap(buf) {
    const levels = [];
    let bucket = MIP_BASE;
    const n0 = Math.ceil(buf.length / bucket);
    let L = { bucket, min: new Float32Array(n0), max: new Float32Array(n0), sq: new Float64Array(n0) };
    for (let b = 0; b < n0; b++) {
      let lo = Infinity, hi = -Infinity, sq = 0;
      const end = Math.min(buf.length, (b + 1) * bucket);
      for (let i = b * bucket; i < end; i++) { const v = buf[i]; if (v < lo) lo = v; if (v > hi) hi = v; sq += v * v; }
      L.min[b] = lo === Infinity ? 0 : lo; L.max[b] = hi === -Infinity ? 0 : hi; L.sq[b] = sq;
    }
    levels.push(L);
    while (L.min.length > 64) {
      bucket *= 4;
      const n = Math.ceil(L.min.length / 4);
      const N = { bucket, min: new Float32Array(n), max: new Float32Array(n), sq: new Float64Array(n) };
      for (let b = 0; b < n; b++) {
        let lo = Infinity, hi = -Infinity, sq = 0;
        const end = Math.min(L.min.length, (b + 1) * 4);
        for (let i = b * 4; i < end; i++) { if (L.min[i] < lo) lo = L.min[i]; if (L.max[i] > hi) hi = L.max[i]; sq += L.sq[i]; }
        N.min[b] = lo; N.max[b] = hi; N.sq[b] = sq;
      }
      L = N; levels.push(L);
    }
    return { frames: buf.length, levels };
  }

  // [min, max, rms] over frames [a, b) using the mipmap, with the raw
  // buffer for the partial buckets at the edges.
  function peakRange(buf, mip, a, b) {
    a = Math.max(0, a | 0); b = Math.min(buf.length, b | 0);
    if (b <= a) return [0, 0, 0];
    const span = b - a;
    let lvl = null;
    for (const L of mip.levels) if (L.bucket <= span / 2) lvl = L; else break;
    let lo = Infinity, hi = -Infinity, sq = 0;
    const raw = (i0, i1) => { for (let i = i0; i < i1; i++) { const v = buf[i]; if (v < lo) lo = v; if (v > hi) hi = v; sq += v * v; } };
    if (!lvl) raw(a, b);
    else {
      const first = Math.ceil(a / lvl.bucket), last = Math.floor(b / lvl.bucket);
      raw(a, Math.min(b, first * lvl.bucket));
      for (let k = first; k < last; k++) { if (lvl.min[k] < lo) lo = lvl.min[k]; if (lvl.max[k] > hi) hi = lvl.max[k]; sq += lvl.sq[k]; }
      raw(Math.max(a, last * lvl.bucket), b);
    }
    return [lo === Infinity ? 0 : lo, hi === -Infinity ? 0 : hi, Math.sqrt(sq / span)];
  }

  // Nearest zero crossing to frame i within +/- window, for click-free cuts.
  function nearestZeroCrossing(buf, i, window = 480) {
    i = Math.max(0, Math.min(buf.length - 1, i | 0));
    for (let d = 0; d <= window; d++) {
      for (const j of [i - d, i + d]) {
        if (j > 0 && j < buf.length && (buf[j - 1] <= 0) !== (buf[j] <= 0)) return j;
      }
    }
    return i;
  }

  return {
    downmix, peak, dbToGain, gainToDb, applyGain, normalize, fade, toInt16,
    fromInt16, mix, clippedCount, peaksMipmap, peakRange, nearestZeroCrossing,
    MIP_BASE,
  };
})();

// ---- wav.js ----------------------------------------------------------------
// WAV Player - Sample Editor: RIFF/WAVE reader and writer.
//
// parse() is a line-for-line port of the chunk walk and the five checks in
// loadSampleToSdram() (VModPro/main.cpp), so its verdict on a file is the
// verdict the device would print. write() emits the plain 44-byte header
// and nothing else.
const Wav = (() => {
  const V = Rules.VERDICT;

  const rd16 = (u8, p) => u8[p] | (u8[p + 1] << 8);
  const rd32 = (u8, p) => (u8[p] | (u8[p + 1] << 8) | (u8[p + 2] << 16) | (u8[p + 3] << 24)) >>> 0;
  const tag = (u8, p, s) => u8[p] === s.charCodeAt(0) && u8[p + 1] === s.charCodeAt(1)
    && u8[p + 2] === s.charCodeAt(2) && u8[p + 3] === s.charCodeAt(3);

  // Parse the bytes of a WAV file.
  //   u8:       the bytes available (may be a prefix of the file)
  //   fileSize: the true file length; defaults to u8.length
  // Returns { ok: true, ...format } or { ok: false, code, verdict }, or
  // { needBytes } when the walk ran past the prefix before finding both
  // the fmt and data chunks, so a caller reading a card can fetch more.
  function parse(u8, fileSize) {
    if (fileSize === undefined) fileSize = u8.length;
    // A failure after the fmt chunk was read carries the format fields too,
    // so the page can say "24-bit stereo at 44.1 kHz" next to the verdict.
    const fail = (code, fmt) => Object.assign({ ok: false, code, verdict: V[code] }, fmt || {});
    if (u8.length < 12) return fileSize < 12 ? fail('BAD_HEADER') : { needBytes: 12 };
    if (!tag(u8, 0, 'RIFF') || !tag(u8, 8, 'WAVE')) return fail('BAD_HEADER');

    // The RIFF size field is ignored (often wrong); the walk is bounded by
    // the real file length, and each chunk size is clamped to what is on
    // the card, so a garbage size can neither loop nor overrun.
    let haveFmt = false, haveData = false;
    let audioFormat = 0, channels = 0, bitsPerSample = 0;
    let rate = 0, dataOfs = 0, dataSize = 0;
    let pos = 12;
    while (pos + 8 <= fileSize && !(haveFmt && haveData)) {
      if (pos + 8 > u8.length) return { needBytes: pos + 8 };
      const body = pos + 8;
      let csize = rd32(u8, pos + 4);
      if (csize > fileSize - body) csize = fileSize - body;
      if (tag(u8, pos, 'fmt ')) {
        const want = csize >= 26 ? 26 : 16;
        if (csize < 16) return fail('BAD_HEADER');
        if (body + want > u8.length) return { needBytes: body + want };
        audioFormat = rd16(u8, body);
        channels = rd16(u8, body + 2);
        rate = rd32(u8, body + 4);
        bitsPerSample = rd16(u8, body + 14);
        // WAVE_FORMAT_EXTENSIBLE keeps the real tag in the SubFormat GUID
        if (audioFormat === 0xFFFE && csize >= 40) {
          if (body + 26 > u8.length) return { needBytes: body + 26 };
          audioFormat = rd16(u8, body + 24);
        }
        haveFmt = true;
      } else if (tag(u8, pos, 'data')) {
        dataOfs = body; dataSize = csize; haveData = true;
      }
      const next = body + csize + (csize & 1);   // RIFF pads odd chunks
      if (next <= pos) break;
      pos = next;
    }
    const fmt = haveFmt ? { format: audioFormat, channels, rate, bits: bitsPerSample } : null;
    if (!haveFmt || !haveData) return fail('BAD_HEADER', fmt);
    if (bitsPerSample !== 16) return fail('BAD_BITS', fmt);
    if (audioFormat !== 1) return fail('NOT_PCM', fmt);
    if (channels !== 1 && channels !== 2) return fail('BAD_CHANNELS', fmt);
    if (rate < Rules.RATE_MIN || rate > Rules.RATE_MAX) return fail('BAD_RATE', fmt);

    let samples = Math.floor(dataSize / 2);
    samples -= samples % channels;
    if (samples === 0) return fail('EMPTY_DATA', fmt);
    const bytesNeeded = samples * 2;           // what the device allocates
    const frames = samples / channels;
    return {
      ok: true, code: 'OK', verdict: '', format: audioFormat, channels, rate,
      bits: bitsPerSample, dataOffset: dataOfs, dataBytes: dataSize,
      bytesNeeded, frames, seconds: frames / rate,
    };
  }

  // Decode the PCM of a parsed file into Float32 channels (for files the
  // browser might not decode itself, and for the tests).
  function pcm(u8, info) {
    const out = [];
    for (let c = 0; c < info.channels; c++) out.push(new Float32Array(info.frames));
    let p = info.dataOffset;
    for (let f = 0; f < info.frames; f++) {
      for (let c = 0; c < info.channels; c++) {
        let v = rd16(u8, p); if (v >= 0x8000) v -= 0x10000;
        out[c][f] = v / 32768; p += 2;
      }
    }
    return out;
  }

  // Canonical 16-bit PCM mono WAV. Header is the classic 44 bytes: RIFF,
  // fmt (16 bytes, tag 1), data. Nothing the device has to skip.
  function write(int16, rate) {
    const dataBytes = int16.length * 2;
    const buf = new ArrayBuffer(44 + dataBytes);
    const dv = new DataView(buf);
    const str = (p, s) => { for (let i = 0; i < 4; i++) dv.setUint8(p + i, s.charCodeAt(i)); };
    const channels = Rules.CHANNELS, bits = Rules.BITS;
    str(0, 'RIFF'); dv.setUint32(4, 36 + dataBytes, true); str(8, 'WAVE');
    str(12, 'fmt '); dv.setUint32(16, 16, true);
    dv.setUint16(20, 1, true);                          // PCM
    dv.setUint16(22, channels, true);
    dv.setUint32(24, rate, true);
    dv.setUint32(28, rate * channels * (bits / 8), true); // byte rate
    dv.setUint16(32, channels * (bits / 8), true);        // block align
    dv.setUint16(34, bits, true);
    str(36, 'data'); dv.setUint32(40, dataBytes, true);
    new Int16Array(buf, 44).set(int16);
    return buf;
  }

  return { parse, pcm, write };
})();

// ---- audio.js --------------------------------------------------------------
// WAV Player - Sample Editor: everything that needs Web Audio. Decoding, resampling,
// preview playback, recording, and the export render. Sample math lives in
// dsp.js; this file only moves buffers in and out of the browser.
const Audio = (() => {
  const RATE = Rules.DEVICE_RATE;
  const AC = typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext);
  const OAC = typeof window !== 'undefined' && (window.OfflineAudioContext || window.webkitOfflineAudioContext);

  let ctx = null, master = null, analyser = null;
  // The live context is created on the first user gesture (autoplay policy)
  // at the device rate, so preview buffers are never resampled on the way
  // out. Safari may ignore the request; playSegment copes with either.
  function context() {
    if (!ctx) {
      ctx = new AC({ sampleRate: RATE });
      master = ctx.createGain();
      analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      master.connect(analyser).connect(ctx.destination);
    }
    if (ctx.state === 'suspended') ctx.resume();
    return ctx;
  }

  // Peak level of what is currently playing, 0..1, for meters.
  function outputPeak() {
    if (!analyser) return 0;
    const buf = new Float32Array(analyser.fftSize);
    analyser.getFloatTimeDomainData(buf);
    return Dsp.peak(buf);
  }

  // Resample a mono buffer with the browser's offline renderer (windowed
  // sinc in every current engine, far better than the device's linear
  // interpolation, which is the whole argument for exporting at 48 kHz).
  async function resample(mono, fromRate, toRate) {
    if (fromRate === toRate) return mono;
    const frames = Math.ceil(mono.length * toRate / fromRate);
    const off = new OAC(1, frames, toRate);
    const b = off.createBuffer(1, mono.length, fromRate);
    b.copyToChannel(mono, 0);
    const src = off.createBufferSource();
    src.buffer = b; src.connect(off.destination); src.start(0);
    const out = await off.startRendering();
    return new Float32Array(out.getChannelData(0).subarray(0, frames));
  }

  // Duration from the media element before committing to a decode, so a
  // two-hour podcast is refused without first filling the tab with floats.
  function probeDuration(file) {
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const el = document.createElement('audio');
      el.preload = 'metadata';
      const done = (v) => { URL.revokeObjectURL(url); resolve(v); };
      el.onloadedmetadata = () => done(isFinite(el.duration) ? el.duration : null);
      el.onerror = () => done(null);
      setTimeout(() => done(null), 4000);
      el.src = url;
    });
  }

  const looksLikeWav = (u8) => u8.length >= 12 && u8[0] === 82 && u8[1] === 73 && u8[2] === 70 && u8[3] === 70
    && u8[8] === 87 && u8[9] === 65 && u8[10] === 86 && u8[11] === 69;

  // Decode any file the browser can play into the studio's one format: mono
  // float at the device rate. Returns
  //   { buffer, srcChannels, srcSeconds, wav: parse result or null, kind }
  // wav is the device's verdict on the file exactly as uploaded, so the
  // library can say "as-is: Need 16-bit WAV".
  async function decodeFile(file) {
    const bytes = await file.arrayBuffer();
    const u8 = new Uint8Array(bytes);
    const wav = looksLikeWav(u8) ? Wav.parse(u8) : null;
    // Name the format by extension first: MIME subtypes are things like
    // "x-m4a" and "mpeg", which is not what the user calls the file.
    const ext = (file.name.match(/\.([A-Za-z0-9]{1,5})$/) || [])[1];
    const kind = wav ? 'WAV' : (ext || file.type.split('/')[1] || 'audio').toUpperCase();

    const probed = wav && wav.ok ? wav.seconds : await probeDuration(file);
    if (probed !== null && probed > Rules.MAX_INPUT_SECONDS)
      throw new Error(`That is ${Rules.formatDuration(probed)} long. The device holds ${Rules.formatDuration(Rules.POOL_SECONDS_48K)} in total, so files over 30 minutes are not accepted.`);

    let ab = null;
    try {
      // decodeAudioData resamples to the context rate, so decoding through
      // an offline context at 48 kHz does the conversion in one step.
      const probe = new OAC(1, 1, RATE);
      ab = await new Promise((res, rej) => probe.decodeAudioData(bytes.slice(0), res, rej));
    } catch (e) {
      ab = null;
    }
    let mono, srcChannels;
    if (ab) {
      if (ab.duration > Rules.MAX_INPUT_SECONDS)
        throw new Error(`That is ${Rules.formatDuration(ab.duration)} long; files over 30 minutes are not accepted.`);
      const chans = [];
      for (let c = 0; c < ab.numberOfChannels; c++) chans.push(ab.getChannelData(c));
      mono = chans.length === 1 ? new Float32Array(chans[0]) : Dsp.downmix(chans);
      srcChannels = ab.numberOfChannels;
      if (ab.sampleRate !== RATE) mono = await resample(mono, ab.sampleRate, RATE);
    } else if (wav && wav.ok) {
      // The browser refused a WAV our own reader understands (seen with
      // some EXTENSIBLE headers). Read the PCM ourselves.
      const chans = Wav.pcm(u8, wav);
      mono = chans.length === 1 ? chans[0] : Dsp.downmix(chans);
      srcChannels = wav.channels;
      if (wav.rate !== RATE) mono = await resample(mono, wav.rate, RATE);
    } else {
      throw new Error(`This browser cannot decode ${kind} files. Convert it to WAV, MP3 or M4A in another app and try again.`);
    }
    if (!mono.length) throw new Error('The file decoded to nothing.');
    return { buffer: mono, srcChannels, srcSeconds: mono.length / RATE, wav, kind };
  }

  // Render an export exactly as the device will receive it.
  // s: { inFrame, outFrame, fadeInMs, fadeOutMs, gainDb, normalize, rate }
  // Order: trim, fades, gain, normalize, resample, dither to 16-bit.
  async function renderExport(buffer, s) {
    let seg = new Float32Array(buffer.subarray(s.inFrame, s.outFrame));
    Dsp.fade(seg, Math.round((s.fadeInMs || 0) * RATE / 1000), Math.round((s.fadeOutMs || 0) * RATE / 1000));
    if (s.gainDb) Dsp.applyGain(seg, Dsp.dbToGain(s.gainDb));
    if (s.normalize) Dsp.normalize(seg, -1);
    const rate = s.rate || RATE;
    if (rate !== RATE) seg = await resample(seg, RATE, rate);
    const i16 = Dsp.toInt16(seg, true);
    const clipped = Dsp.clippedCount(seg);
    return {
      wav: Wav.write(i16, rate), rate, frames: i16.length, bytes: i16.length * 2,
      seconds: i16.length / rate, peakDb: Dsp.gainToDb(Dsp.peak(seg)), clipped,
    };
  }

  // The same chain without the final quantize, for previewing an export
  // (fades, gain, normalize audible) at the device rate.
  function previewBuffer(buffer, s) {
    const seg = new Float32Array(buffer.subarray(s.inFrame, s.outFrame));
    Dsp.fade(seg, Math.round((s.fadeInMs || 0) * RATE / 1000), Math.round((s.fadeOutMs || 0) * RATE / 1000));
    if (s.gainDb) Dsp.applyGain(seg, Dsp.dbToGain(s.gainDb));
    if (s.normalize) Dsp.normalize(seg, -1);
    return seg;
  }

  // Play a mono 48 kHz buffer. loop: true plays it the way the device loops,
  // a hard jump back to the first frame with no crossfade. Returns a handle:
  //   stop(), position() in seconds within the buffer, done (Promise).
  function playSegment(mono, { loop = false, gain = 1, offset = 0 } = {}) {
    const c = context();
    const ab = c.createBuffer(1, mono.length, RATE);
    ab.copyToChannel(mono, 0);
    const src = c.createBufferSource();
    src.buffer = ab; src.loop = loop;
    const g = c.createGain(); g.gain.value = gain;
    src.connect(g).connect(master);
    const t0 = c.currentTime;
    offset = Math.max(0, Math.min(offset, ab.duration));
    src.start(0, offset);
    let stopped = false;
    const done = new Promise((res) => { src.onended = () => { stopped = true; res(); }; });
    return {
      stop() { if (!stopped) { stopped = true; try { src.stop(); } catch (e) { /* already ended */ } } },
      position() {
        if (stopped) return null;
        const t = offset + (c.currentTime - t0);
        return loop ? t % ab.duration : Math.min(t, ab.duration);
      },
      get playing() { return !stopped; },
      duration: ab.duration, done,
    };
  }

  // ---- Recording ----------------------------------------------------------
  // Raw float capture through an AudioWorklet, so the take never passes
  // through the lossy codec MediaRecorder would use. Processing (echo
  // cancellation, noise suppression, auto gain) is switched off: those are
  // for calls, and they chew on sound effects.
  const WORKLET_SRC = `
    class VsCapture extends AudioWorkletProcessor {
      process(inputs) {
        const ch = inputs[0] && inputs[0][0];
        if (ch) this.port.postMessage(new Float32Array(ch));
        return true;
      }
    }
    registerProcessor('vs-capture', VsCapture);`;
  let workletReady = null;

  async function startRecording(onLevel) {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false, channelCount: 1 },
    });
    const c = context();
    const src = c.createMediaStreamSource(stream);
    const chunks = [];
    let node, proc = null;
    if (c.audioWorklet) {
      if (!workletReady) {
        const url = URL.createObjectURL(new Blob([WORKLET_SRC], { type: 'application/javascript' }));
        workletReady = c.audioWorklet.addModule(url);
      }
      await workletReady;
      node = new AudioWorkletNode(c, 'vs-capture', { numberOfInputs: 1, numberOfOutputs: 0, channelCount: 1 });
      node.port.onmessage = (e) => { chunks.push(e.data); if (onLevel) onLevel(Dsp.peak(e.data)); };
      src.connect(node);
    } else {
      // Old Safari: ScriptProcessor, deprecated but functional
      proc = c.createScriptProcessor(4096, 1, 1);
      proc.onaudioprocess = (e) => { const d = new Float32Array(e.inputBuffer.getChannelData(0)); chunks.push(d); if (onLevel) onLevel(Dsp.peak(d)); };
      const sink = c.createGain(); sink.gain.value = 0;
      src.connect(proc).connect(sink).connect(c.destination);
      node = proc;
    }
    const t0 = c.currentTime;
    return {
      elapsed: () => c.currentTime - t0,
      async stop() {
        try { src.disconnect(); node.disconnect(); } catch (e) { /* ignore */ }
        stream.getTracks().forEach((t) => t.stop());
        let n = 0; for (const ch of chunks) n += ch.length;
        let mono = new Float32Array(n); let p = 0;
        for (const ch of chunks) { mono.set(ch, p); p += ch.length; }
        if (c.sampleRate !== RATE) mono = await resample(mono, c.sampleRate, RATE);
        return mono;
      },
    };
  }

  return { RATE, context, outputPeak, resample, decodeFile, renderExport, previewBuffer, playSegment, startRecording, supported: !!(AC && OAC) };
})();

// ---- card.js ---------------------------------------------------------------
// WAV Player - Sample Editor: the SD card, through the File System Access API.
//
// Chrome and Edge let a page hold a directory handle the user picked and
// read and write inside it. The page asks for the card itself and finds
// /VMOD/samples on it. Safari and Firefox have no such API: they get
// download() and the folder path spelled out. Nothing here formats
// anything; a browser cannot, and the device does it properly itself.
const Card = (() => {

  const supported = typeof window !== 'undefined' && typeof window.showDirectoryPicker === 'function';

  // state: 'none' (no card), 'unset' (folder picked but no VMOD/samples on
  // it), 'ready' (samples folder in hand), 'stale' (a remembered handle
  // that needs a click to re-authorize).
  const st = { state: 'none', root: null, samples: null, label: '' };

  async function permitted(handle, ask) {
    const opts = { mode: 'readwrite' };
    if ((await handle.queryPermission(opts)) === 'granted') return true;
    if (ask && (await handle.requestPermission(opts)) === 'granted') return true;
    return false;
  }

  // Resolve a picked folder to the samples directory. Accepts the card
  // root, the VMOD folder, or the samples folder itself.
  async function adopt(handle) {
    st.root = handle; st.samples = null; st.label = handle.name;
    const lower = handle.name.toLowerCase();
    try {
      if (lower === 'samples') st.samples = handle;
      else {
        const vmod = lower === 'vmod' ? handle : await handle.getDirectoryHandle('VMOD');
        st.samples = await vmod.getDirectoryHandle('samples');
      }
    } catch (e) { st.samples = null; }
    st.state = st.samples ? 'ready' : 'unset';
    return st.state;
  }

  async function connect() {
    const h = await window.showDirectoryPicker({ id: 'vmod-card', mode: 'readwrite' });
    return adopt(h);
  }

  // Re-adopt a handle restored from IndexedDB. Without a user gesture the
  // browser only answers queryPermission; requestPermission needs a click,
  // so a 'prompt' result parks the card as 'stale' until the user hits
  // Reconnect.
  async function restore(handle, ask) {
    if (!handle) return 'none';
    if (!(await permitted(handle, ask))) { st.root = handle; st.state = 'stale'; st.label = handle.name; return st.state; }
    return adopt(handle);
  }

  function disconnect() { st.state = 'none'; st.root = null; st.samples = null; st.label = ''; }

  // Read the header of a file on the card without pulling the whole thing
  // in: 4 KB first, then whatever the chunk walk says it still needs.
  async function inspect(file) {
    let want = 4096;
    for (;;) {
      const u8 = new Uint8Array(await file.slice(0, Math.min(want, file.size)).arrayBuffer());
      const r = Wav.parse(u8, file.size);
      if (r.needBytes === undefined) return r;
      if (want >= file.size) return { ok: false, code: 'BAD_HEADER', verdict: Rules.VERDICT.BAD_HEADER };
      want = Math.max(want * 4, r.needBytes + 64);
    }
  }

  // Everything in the samples folder, with the device's view of each file.
  async function list() {
    if (st.state !== 'ready') return [];
    const out = [];
    for await (const [name, entry] of st.samples.entries()) {
      if (entry.kind !== 'file') continue;
      const file = await entry.getFile();
      const listable = Rules.isListable(name);
      const item = { name, size: file.size, listable, info: null, modified: file.lastModified };
      if (listable) {
        try { item.info = await inspect(file); } catch (e) { item.info = { ok: false, code: 'READ_FAILED', verdict: Rules.VERDICT.READ_FAILED }; }
      }
      out.push(item);
    }
    out.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    return out;
  }

  // FAT names are case-insensitive: LASER.wav and laser.wav are one file.
  async function existing(name) {
    if (st.state !== 'ready') return null;
    const lower = name.toLowerCase();
    for await (const [n, entry] of st.samples.entries())
      if (entry.kind === 'file' && n.toLowerCase() === lower) return n;
    return null;
  }

  async function write(name, arrayBuffer) {
    if (st.state !== 'ready') throw new Error('No card connected.');
    const fh = await st.samples.getFileHandle(name, { create: true });
    const w = await fh.createWritable();
    await w.write(arrayBuffer);
    await w.close();
  }

  async function remove(name) {
    if (st.state !== 'ready') throw new Error('No card connected.');
    await st.samples.removeEntry(name);
  }

  async function readFile(name) {
    const fh = await st.samples.getFileHandle(name);
    return fh.getFile();
  }

  // Fallback for browsers without card access, and an explicit choice for
  // anyone who prefers it.
  function download(name, arrayBuffer) {
    const blob = new Blob([arrayBuffer], { type: 'audio/wav' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = name; a.style.display = 'none';
    document.body.appendChild(a); a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1500);
  }

  return { supported, st, connect, restore, adopt, disconnect, list, existing, write, remove, readFile, download, inspect };
})();

// ---- store.js --------------------------------------------------------------
// WAV Player - Sample Editor: persistence in IndexedDB so a reload does not lose
// the session. Original files are kept as blobs and re-decoded on load;
// decoded float audio is too big to store. The card's directory handle is
// stored too, which is what makes "Reconnect" a single click.
const Store = (() => {
  const DB = 'vmod-studio', VERSION = 1;
  let dbp = null;

  function open() {
    if (dbp) return dbp;
    dbp = new Promise((res, rej) => {
      if (typeof indexedDB === 'undefined') { rej(new Error('no IndexedDB')); return; }
      const req = indexedDB.open(DB, VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains('sources')) db.createObjectStore('sources', { keyPath: 'id' });
        if (!db.objectStoreNames.contains('kv')) db.createObjectStore('kv');
      };
      req.onsuccess = () => res(req.result);
      req.onerror = () => rej(req.error);
    });
    return dbp;
  }

  const tx = (db, store, mode, fn) => new Promise((res, rej) => {
    const t = db.transaction(store, mode);
    const r = fn(t.objectStore(store));
    t.oncomplete = () => res(r && r.result !== undefined ? r.result : undefined);
    t.onerror = () => rej(t.error);
    t.onabort = () => rej(t.error);
  });

  async function putSource(rec) { const db = await open(); return tx(db, 'sources', 'readwrite', (s) => s.put(rec)); }
  async function delSource(id) { const db = await open(); return tx(db, 'sources', 'readwrite', (s) => s.delete(id)); }
  async function allSources() {
    const db = await open();
    return new Promise((res, rej) => {
      const r = db.transaction('sources').objectStore('sources').getAll();
      r.onsuccess = () => res(r.result || []); r.onerror = () => rej(r.error);
    });
  }
  async function get(key) {
    const db = await open();
    return new Promise((res, rej) => {
      const r = db.transaction('kv').objectStore('kv').get(key);
      r.onsuccess = () => res(r.result); r.onerror = () => rej(r.error);
    });
  }
  async function set(key, val) { const db = await open(); return tx(db, 'kv', 'readwrite', (s) => s.put(val, key)); }
  async function clearAll() {
    const db = await open();
    await tx(db, 'sources', 'readwrite', (s) => s.clear());
    await tx(db, 'kv', 'readwrite', (s) => s.clear());
  }

  // Wrap so a missing or blocked IndexedDB (private windows, some embeds)
  // degrades to an in-memory session instead of breaking the page.
  const safe = (fn, fallback) => async (...a) => { try { return await fn(...a); } catch (e) { return fallback; } };

  return {
    putSource: safe(putSource), delSource: safe(delSource), allSources: safe(allSources, []),
    get: safe(get, undefined), set: safe(set), clearAll: safe(clearAll),
  };
})();

// ---- waveview.js -----------------------------------------------------------
// WAV Player - Sample Editor: canvas waveform drawing shared by the editor and the
// arranger. Uses the peak mipmap from dsp.js so any zoom level costs one
// lookup per pixel column, not one per sample.
const WaveView = (() => {
  const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);

  // The multiplier the export chain would apply to this range, so the drawn
  // shape matches the file that gets saved. Normalize is on by default, so a
  // quiet recording is drawn at the level it will actually have.
  function displayScale(buffer, mip, a, b, settings) {
    let s = Math.pow(10, (settings.gainDb || 0) / 20);
    if (settings.normalize) {
      const [lo, hi] = Dsp.peakRange(buffer, mip, a, b);
      const pk = Math.max(Math.abs(lo), Math.abs(hi)) * s;
      if (pk > 1e-5) s = Math.pow(10, -1 / 20) / (pk / s);
    }
    return Math.min(64, Math.max(0.05, s));
  }

  // Draw frames [f0, f1) of buffer into the rect (x, y, w, h). `scale` is a
  // display-only multiplier: pass the gain the export will apply so the shape
  // on screen is the shape of the file that comes out.
  function draw(g, buffer, mip, x, y, w, h, f0, f1, color, scale = 1) {
    if (w <= 0 || f1 <= f0) return;
    const fpp = (f1 - f0) / w;
    const mid = y + h / 2, half = (h / 2) * scale;
    g.fillStyle = color;
    if (fpp >= 1.5) {
      for (let px = 0; px < w; px++) {
        const a = Math.floor(f0 + px * fpp), b = Math.max(a + 1, Math.floor(f0 + (px + 1) * fpp));
        const [lo, hi] = Dsp.peakRange(buffer, mip, a, b);
        const top = clamp(mid - hi * half, y, y + h), bot = clamp(mid - lo * half, y, y + h);
        g.fillRect(x + px, top, 1, Math.max(1, bot - top));
      }
    } else {
      // Zoomed in past one frame per pixel: a line through the samples
      g.strokeStyle = color; g.lineWidth = 1.2; g.beginPath();
      const a = Math.max(0, Math.floor(f0)), b = Math.min(buffer.length, Math.ceil(f1) + 1);
      for (let i = a; i < b; i++) {
        const px = x + (i - f0) / fpp, py = clamp(mid - buffer[i] * half, y, y + h);
        if (i === a) g.moveTo(px, py); else g.lineTo(px, py);
      }
      g.stroke();
    }
  }

  // The DAW look: a filled peak envelope (top edge follows the per-pixel
  // maximum, bottom edge the minimum) with a brighter RMS body inside it.
  // Past one frame per pixel it falls back to the sample line.
  function drawFilled(g, buffer, mip, x, y, w, h, f0, f1, peakColor, centreColor, rmsColor, scale = 1) {
    if (w <= 0 || f1 <= f0) return;
    const fpp = (f1 - f0) / w;
    const mid = y + h / 2, half = (h / 2) * scale;
    if (centreColor) { g.fillStyle = centreColor; g.fillRect(x, Math.round(mid), w, 1); }
    if (fpp < 1.5) { draw(g, buffer, mip, x, y, w, h, f0, f1, rmsColor || peakColor, scale); return; }
    const n = Math.ceil(w);
    const tops = new Float32Array(n), bots = new Float32Array(n), rms = new Float32Array(n);
    for (let px = 0; px < n; px++) {
      const a = Math.floor(f0 + px * fpp), b = Math.max(a + 1, Math.floor(f0 + (px + 1) * fpp));
      const [lo, hi, r] = Dsp.peakRange(buffer, mip, a, b);
      tops[px] = clamp(mid - Math.max(hi, 0.004) * half, y, y + h);   // never thinner than a hairline
      bots[px] = clamp(mid - Math.min(lo, -0.004) * half, y, y + h);
      rms[px] = Math.min(Math.max(r, 0.003) * half, h / 2);
    }
    const envelope = (top, bot) => {
      g.beginPath(); g.moveTo(x, top(0));
      for (let px = 1; px < n; px++) g.lineTo(x + px, top(px));
      for (let px = n - 1; px >= 0; px--) g.lineTo(x + px, bot(px));
      g.closePath(); g.fill();
    };
    g.fillStyle = peakColor; envelope((i) => tops[i], (i) => bots[i]);
    if (rmsColor) { g.fillStyle = rmsColor; envelope((i) => mid - rms[i], (i) => mid + rms[i]); }
  }

  // Size a canvas for the device pixel ratio and return its 2D context
  // scaled so drawing code works in CSS pixels.
  function fit(canvas, cssW, cssH) {
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const pw = Math.max(1, Math.round(cssW * dpr)), ph = Math.max(1, Math.round(cssH * dpr));
    if (canvas.width !== pw || canvas.height !== ph) { canvas.width = pw; canvas.height = ph; }
    canvas.style.width = cssW + 'px'; canvas.style.height = cssH + 'px';
    const g = canvas.getContext('2d');
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    return g;
  }

  // Nice tick spacing for a time ruler: seconds per tick for a pixel scale.
  function tickStep(pxPerSec, minPx = 70) {
    const steps = [0.01, 0.02, 0.05, 0.1, 0.2, 0.5, 1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1200, 1800, 3600];
    for (const s of steps) if (s * pxPerSec >= minPx) return s;
    return Math.ceil(minPx / pxPerSec / 3600) * 3600;   // whole hours for very long projects
  }

  function timeLabel(sec, step) {
    const m = Math.floor(sec / 60), s = sec - m * 60;
    if (step < 1) return `${m}:${s.toFixed(step < 0.1 ? 2 : 1).padStart(step < 0.1 ? 5 : 4, '0')}`;
    return `${m}:${Math.round(s).toString().padStart(2, '0')}`;
  }

  return { draw, drawFilled, displayScale, fit, tickStep, timeLabel };
})();

// ---- tip.js ----------------------------------------------------------------
// WAV Player - Sample Editor: hover hints. One tooltip element for the whole page,
// driven by data-tip on any control. It shows after a short hover, on
// keyboard focus, and on a long press; it hides on leave, click, key, or
// scroll. Plain text only. It sits above the control and flips below when
// there is no room.
const Tip = (() => {
  const DELAY = 350, PRESS = 450, GAP = 8, MARGIN = 8;
  let root = null, tip = null, target = null, armedFor = null, timer = 0, pressTimer = 0;
  let enabled = true;   // user switch; the "?" chips keep working when this is off

  function mount(container) {
    root = container;
    tip = document.createElement('div');
    tip.className = 'vs-tip'; tip.hidden = true; tip.setAttribute('role', 'tooltip');
    tip.style.fontFamily = getComputedStyle(root).fontFamily;
    document.body.append(tip);
    root.addEventListener('mouseover', (e) => { const t = find(e.target); if (t && t !== target && t !== armedFor) arm(t); });
    root.addEventListener('mouseout', (e) => { const t = find(e.target); if (t && !(e.relatedTarget && t.contains(e.relatedTarget))) hide(); });
    root.addEventListener('focusin', (e) => { const t = find(e.target); if (t && t.matches(':focus-visible')) show(t); });
    root.addEventListener('focusout', hide);
    root.addEventListener('mousedown', hide, true);
    root.addEventListener('touchstart', (e) => { clearTimeout(pressTimer); const t = find(e.target); if (t) pressTimer = setTimeout(() => show(t), PRESS); }, { passive: true });
    root.addEventListener('touchend', () => clearTimeout(pressTimer), { passive: true });
    root.addEventListener('touchmove', () => { clearTimeout(pressTimer); hide(); }, { passive: true });
    document.addEventListener('keydown', hide);
    window.addEventListener('scroll', hide, { passive: true, capture: true });
    window.addEventListener('resize', hide);
  }
  function find(node) {
    if (!node || !node.closest) return null;
    const t = node.closest('[data-tip]');
    if (!t || !root.contains(t)) return null;
    return enabled || t.classList.contains('vs-help') ? t : null;
  }
  function arm(t) { clearTimeout(timer); armedFor = t; timer = setTimeout(() => { if (armedFor === t) show(t); }, DELAY); }
  function show(t) {
    clearTimeout(timer); armedFor = null;
    const text = t.dataset.tip; if (!text) return;
    target = t;
    tip.textContent = text;
    tip.hidden = false; tip.classList.remove('vs-tip-below');
    const r = t.getBoundingClientRect();
    const vw = window.innerWidth, tw = tip.offsetWidth, th = tip.offsetHeight;
    let x = r.left + r.width / 2 - tw / 2;
    x = Math.max(MARGIN, Math.min(vw - tw - MARGIN, x));
    let y = r.top - th - GAP;
    if (y < MARGIN) { y = r.bottom + GAP; tip.classList.add('vs-tip-below'); }
    tip.style.left = x + 'px'; tip.style.top = y + 'px';
    tip.style.setProperty('--vs-tip-ax', Math.max(10, Math.min(tw - 10, r.left + r.width / 2 - x)) + 'px');
  }
  function hide() { clearTimeout(timer); clearTimeout(pressTimer); armedFor = null; target = null; if (tip) tip.hidden = true; }
  // Attach a hint and hand the element back, so a call can sit inside append().
  function set(node, text) { node.dataset.tip = text; return node; }
  // A small "?" chip carrying a hint, for things that have no control of their own.
  function chip(text) {
    const c = document.createElement('span');
    c.className = 'vs-help'; c.textContent = '?'; c.tabIndex = 0; c.setAttribute('aria-label', 'Help'); c.dataset.tip = text;
    return c;
  }
  function enable(on) { enabled = !!on; if (!enabled) hide(); }
  return { mount, set, chip, hide, enable, get enabled() { return enabled; }, get target() { return target; } };
})();

// ---- editor.js -------------------------------------------------------------
// WAV Player - Sample Editor: the single-clip editor. One source at a time: waveform
// with zoom, in and out handles, fades, gain, normalize, rate, name, and the
// export button. All edits live in source.settings and are committed
// through the app for undo.
const Editor = (() => {
  const RATE = Rules.DEVICE_RATE;
  const tip = Tip.set;
  const WAVE_H = 300, HANDLE_PX = 7;

  let api, root, els = {}, src = null;
  let view = { f0: 0, f1: 1 };        // visible frame range
  let playhead = 0;                    // frame
  let drag = null, player = null, raf = 0;
  let snapZero = true;

  const el = (tag, cls, html) => { const e = document.createElement(tag); if (cls) e.className = cls; if (html !== undefined) e.innerHTML = html; return e; };
  const fmt = (frames, ms) => Rules.formatDuration(frames / RATE, ms);
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  function mount(container, appApi) {
    api = appApi; root = container;
    root.innerHTML = '';
    els.empty = el('div', 'vs-empty', 'Click Add files, or drop an audio file anywhere on the page, to start.');
    els.wrap = el('div', 'vs-editor'); els.wrap.hidden = true;

    // Transport. Loop, Fit and Zoom are advanced-only.
    const tr = el('div', 'vs-row vs-transport');
    els.play = tip(btn('Play', 'vs-btn vs-btn-primary', () => togglePlay(false)), 'Play from the red line. The space bar also plays and stops.');
    els.loop = tip(btn('Loop preview', 'vs-btn vs-adv', () => togglePlay(true)), 'Play the kept part over and over, jumping straight back to the start with no smoothing, exactly as the VMod Pro loops. If you hear a click here you will hear it there. Loop-safe fixes that.');
    els.stop = tip(btn('Stop', 'vs-btn', stop), 'Stop playing.');
    els.time = el('span', 'vs-time', '0:00.00');
    els.fit = tip(btn('Fit', 'vs-btn vs-btn-sm vs-adv', () => { setView(0, src.buffer.length); }), 'Zoom out to see the whole file.');
    els.zoomSel = tip(btn('Zoom to selection', 'vs-btn vs-btn-sm vs-adv', () => { const s = src.settings; setView(s.inFrame, s.outFrame, 0.06); }), 'Zoom in on the kept part, between the two white handles.');
    els.help = Tip.chip('Drag the white handles to choose what gets saved. Click to move the red line, drag across to pick a section. Scroll to zoom, hold Shift and scroll to move along.');
    tr.append(els.play, els.loop, els.stop, els.time, el('span', 'vs-spacer'), els.fit, els.zoomSel, els.help);

    // Canvas
    els.canvasWrap = el('div', 'vs-wave-wrap');
    els.canvas = el('canvas', 'vs-wave');
    els.canvasWrap.append(els.canvas);

    // Trim row (advanced)
    const trim = el('div', 'vs-row vs-adv');
    els.inLbl = tip(el('span', 'vs-kv', ''), 'Where the saved part starts.');
    els.outLbl = tip(el('span', 'vs-kv', ''), 'Where the saved part ends.');
    els.setIn = tip(btn('Set in (I)', 'vs-btn vs-btn-sm', () => setIn(playhead)), 'Start the saved part at the red line. Everything before it is cut off. The I key does the same.');
    els.setOut = tip(btn('Set out (O)', 'vs-btn vs-btn-sm', () => setOut(playhead)), 'End the saved part at the red line. Everything after it is cut off. The O key does the same.');
    els.snap = check('Snap cuts to zero crossings', snapZero, (v) => { snapZero = v; });
    tip(els.snap.wrap, 'Nudges each cut to the nearest spot where the wave crosses the middle line, so the sample does not start or end with a click.');
    trim.append(els.inLbl, els.setIn, els.outLbl, els.setOut, el('span', 'vs-spacer'), els.snap.wrap);

    // Processing row (advanced)
    const proc = el('div', 'vs-row vs-adv');
    els.fadeIn = num('Fade in', 'ms', 0, 5000, 1, (v) => set({ fadeInMs: v }, 'fade in'));
    tip(els.fadeIn.wrap, 'Brings the volume up from silence over this many milliseconds at the start. A few milliseconds hides a click. 1000 ms is one second.');
    els.fadeOut = num('Fade out', 'ms', 0, 5000, 1, (v) => set({ fadeOutMs: v }, 'fade out'));
    tip(els.fadeOut.wrap, 'Takes the volume down to silence over this many milliseconds at the end.');
    els.loopSafe = tip(btn('Loop-safe', 'vs-btn vs-btn-sm', () => set({ fadeInMs: 10, fadeOutMs: 10 }, 'loop-safe fades')), 'Sets a 10 millisecond fade at both ends, enough to hide the seam when the VMod Pro loops the sample.');
    els.gain = range('Gain', -24, 12, 0.5, 'dB', (v) => set({ gainDb: v }, 'gain'));
    tip(els.gain.wrap, 'Volume of the saved file. 0 dB leaves it as it is, minus is quieter, plus is louder. Double-click the slider to reset.');
    els.norm = check('Normalize to -1 dB', true, (v) => set({ normalize: v }, 'normalize'));
    tip(els.norm.wrap, "Turns the whole sound up or down so its loudest moment sits just under the maximum. Quiet recordings become usable and loud ones cannot distort. The VMod Pro's own Volume goes to 200% if a sample still needs lifting. Leave it on unless you have a reason not to.");
    els.rate = select('Rate', [[48000, '48 kHz (device rate, no resampling)'], [24000, '24 kHz (half the memory, for long beds)']], (v) => set({ rate: +v }, 'rate'));
    tip(els.rate.wrap, 'How many snapshots of the sound are stored per second. 48 kHz is what the VMod Pro uses. 24 kHz takes half the memory and loses some brightness, fine for long background sounds.');
    proc.append(els.fadeIn.wrap, els.fadeOut.wrap, els.loopSafe, els.gain.wrap, els.norm.wrap, els.rate.wrap);

    // Name + export row
    const ex = el('div', 'vs-export');
    els.name = el('input', 'vs-name'); els.name.type = 'text'; els.name.maxLength = Rules.BASE_MAX + 4; els.name.spellcheck = false;
    els.name.addEventListener('input', () => { renameLive(els.name.value); });
    els.name.addEventListener('change', () => { rename(els.name.value); });
    els.name.addEventListener('keydown', (e) => { if (e.key === 'Enter') els.name.blur(); e.stopPropagation(); });
    tip(els.name, `The file name on the card. Letters, numbers, dashes and underscores only, no spaces, up to ${Rules.BASE_MAX} characters. ${Rules.BASE_SOFT} or fewer shows in full on the VMod Pro screen.`);
    els.ext = el('span', 'vs-ext', Rules.EXT);
    els.count = tip(el('span', 'vs-count', ''), `Characters used, out of ${Rules.BASE_MAX}.`);
    els.nameMsg = el('div', 'vs-name-msg', '');
    const nameRow = el('div', 'vs-name-row');
    nameRow.append(el('label', 'vs-lbl', 'Name'), els.name, els.ext, els.count);
    // Conversion panel: From (the file as added) and Saves as (the file that
    // comes out), then the note, then the button that makes it. One box, so
    // the button reads as the product of the two lines above it.
    els.plan = el('div', 'vs-plan');
    els.fromV = tip(el('span', 'vs-plan-v', ''), 'The file as you added it: its name, format, and size.');
    els.readout = tip(el('span', 'vs-plan-v vs-readout', ''), `The file that comes out: always a 16-bit mono WAV the VMod Pro can read. The percentage is how much of one preset's sample memory it takes. A preset holds ${Rules.formatBytes(Rules.POOL_BYTES)} of samples in memory, about ${Rules.formatDuration(Rules.POOL_SECONDS_48K)} of audio. Anything longer plays straight from the card instead.`);
    els.note = el('span', 'vs-plan-note', '');
    els.exportBtn = btn('Save WAV to card', 'vs-btn vs-btn-export vs-btn-lg', () => api.exportSource(src, 'card'));
    els.dlBtn = tip(btn('Download WAV', 'vs-btn vs-btn-lg', () => api.exportSource(src, 'download')), 'Save the finished file to your computer instead of the card.');
    const exRow = el('div', 'vs-row');
    exRow.append(els.exportBtn, els.dlBtn);
    els.plan.append(el('span', 'vs-plan-k', 'From'), els.fromV, el('span', 'vs-plan-k', 'Saves as'), els.readout, el('span', 'vs-plan-k', ''), els.note, exRow);
    ex.append(nameRow, els.nameMsg, els.plan);

    els.wrap.append(tr, els.canvasWrap, trim, proc, ex);
    root.append(els.empty, els.wrap);

    bindCanvas();
    window.addEventListener('resize', () => { if (src) render(); });
  }

  // ---- small control builders --------------------------------------------
  function btn(label, cls, fn) { const b = el('button', cls, label); b.type = 'button'; b.addEventListener('click', fn); return b; }
  function check(label, init, fn) {
    const wrap = el('label', 'vs-check'); const i = el('input'); i.type = 'checkbox'; i.checked = init;
    i.addEventListener('change', () => fn(i.checked)); wrap.append(i, document.createTextNode(' ' + label));
    return { wrap, input: i };
  }
  function num(label, unit, min, max, step, fn) {
    const wrap = el('label', 'vs-num'); const i = el('input'); i.type = 'number'; i.min = min; i.max = max; i.step = step;
    i.addEventListener('change', () => { let v = +i.value; if (!isFinite(v)) v = min; v = Math.min(max, Math.max(min, v)); i.value = v; fn(v); });
    i.addEventListener('keydown', (e) => e.stopPropagation());
    wrap.append(document.createTextNode(label + ' '), i, el('span', 'vs-unit', unit));
    return { wrap, input: i };
  }
  function range(label, min, max, step, unit, fn) {
    const wrap = el('label', 'vs-range'); const i = el('input'); i.type = 'range'; i.min = min; i.max = max; i.step = step;
    const out = el('span', 'vs-unit', '');
    i.addEventListener('input', () => { out.textContent = (+i.value > 0 ? '+' : '') + (+i.value).toFixed(1) + ' ' + unit; });
    i.addEventListener('change', () => fn(+i.value));
    i.addEventListener('dblclick', () => { i.value = 0; i.dispatchEvent(new Event('input')); fn(0); });
    wrap.append(document.createTextNode(label + ' '), i, out);
    return { wrap, input: i, out };
  }
  function select(label, opts, fn) {
    const wrap = el('label', 'vs-select'); const s = el('select');
    for (const [v, t] of opts) { const o = el('option', '', t); o.value = v; s.append(o); }
    s.addEventListener('change', () => fn(s.value)); wrap.append(document.createTextNode(label + ' '), s);
    return { wrap, input: s };
  }

  // ---- state ---------------------------------------------------------------
  function show(source) {
    stop();
    src = source;
    els.empty.hidden = !!src; els.wrap.hidden = !src;
    if (!src) return;
    setView(0, src.buffer.length);
    playhead = src.settings.inFrame;
    syncControls();
    render();
  }

  function set(patch, label) {
    if (!src) return;
    Object.assign(src.settings, patch);
    api.commit(label);
    syncControls(); render();
    if (player) restartPlayback();
  }

  function snap(frame) {
    frame = Math.max(0, Math.min(src.buffer.length, Math.round(frame)));
    return snapZero ? Dsp.nearestZeroCrossing(src.buffer, frame, 240) : frame;
  }
  function setIn(f) { f = snap(f); if (f >= src.settings.outFrame) return; set({ inFrame: f }, 'in point'); }
  function setOut(f) { f = snap(f); if (f <= src.settings.inFrame) return; set({ outFrame: f }, 'out point'); }

  function renameLive(v) {
    const st = Rules.nameStatus(v);
    els.count.textContent = `${v.length}/${Rules.BASE_MAX}`;
    els.count.className = 'vs-count vs-' + st.level;
    // The ok message ("Device lists it as x.wav") is what the Saves as line
    // already shows, so only warnings and errors appear here.
    els.nameMsg.textContent = st.level === 'ok' ? '' : st.message; els.nameMsg.className = 'vs-name-msg vs-' + st.level;
  }
  function rename(v) {
    if (!src) return;
    const fixed = v === '' ? src.name : v;
    if (Rules.nameStatus(fixed).level === 'bad') {
      const s = Rules.sanitizeBase(fixed);
      api.toast(`Renamed to ${s}: ${Rules.nameStatus(fixed).message}`);
      src.name = s;
    } else src.name = fixed;
    els.name.value = src.name; renameLive(src.name);
    api.commit('rename'); api.refreshLibrary();
  }

  function syncControls() {
    const s = src.settings;
    els.inLbl.textContent = 'In ' + fmt(s.inFrame, true);
    els.outLbl.textContent = 'Out ' + fmt(s.outFrame, true);
    els.fadeIn.input.value = s.fadeInMs; els.fadeOut.input.value = s.fadeOutMs;
    els.gain.input.value = s.gainDb; els.gain.input.dispatchEvent(new Event('input'));
    els.norm.input.checked = !!s.normalize;
    els.rate.input.value = s.rate;
    els.name.value = src.name; renameLive(src.name);
    const frames = s.outFrame - s.inFrame;
    const outFrames = Math.round(frames * s.rate / RATE);
    const bytes = Rules.bytesForFrames(outFrames);
    const pct = (bytes / Rules.POOL_BYTES) * 100;
    const d = api.describe(src);
    els.fromV.innerHTML = `<span class="vs-mono">${esc(d.origName)}</span> · ${esc(d.source)}`;
    els.readout.innerHTML = `<span class="vs-mono">${esc(Rules.fileName(src.name))}</span> · 16-bit mono WAV · ${(s.rate / 1000).toFixed(0)} kHz · ${Rules.formatDuration(outFrames / s.rate, true)} · ${Rules.formatBytes(bytes)} · ${pct.toFixed(pct < 10 ? 1 : 0)}% of a preset`;
    // Over the pool is no longer a refusal: the VMod Pro streams it from the
    // card. Worth saying, never an error.
    const streamed = bytes > Rules.POOL_BYTES;
    const level = streamed || bytes > Rules.WARN_BYTES ? 'vs-warn' : '';
    els.note.textContent = streamed
      ? `Too long to hold in memory, so the VMod Pro plays it straight from the card. Up to ${Rules.MAX_STREAMS} files can do that at once. ${d.note}`
      : bytes > Rules.WARN_BYTES ? `Uses over half of a preset's sample memory by itself. ${d.note}` : d.note;
    els.readout.className = 'vs-plan-v vs-readout ' + level;
    els.note.className = 'vs-plan-note ' + level;
    const card = api.cardMode();
    els.exportBtn.textContent = card ? 'Save WAV to card' : 'Download WAV';
    els.exportBtn.dataset.tip = card ? `Write the finished file into ${Rules.FOLDER} on the card, ready to pick on the VMod Pro.`
      : `Save the finished file to your computer. Then copy it into the ${Rules.FOLDER} folder on your card.`;
    els.dlBtn.hidden = !card;
    els.exportBtn.disabled = false;
  }

  // ---- view / render -------------------------------------------------------
  function setView(f0, f1, pad = 0) {
    const n = src.buffer.length;
    const span = Math.max(64, f1 - f0);
    f0 -= span * pad; f1 += span * pad;
    f0 = Math.max(0, f0); f1 = Math.min(n, f1);
    if (f1 - f0 < 64) f1 = Math.min(n, f0 + 64);
    view = { f0, f1 };
    render();
  }
  const xOf = (f, w) => (f - view.f0) / (view.f1 - view.f0) * w;
  const fOf = (x, w) => view.f0 + x / w * (view.f1 - view.f0);

  function render() {
    if (!src || els.wrap.hidden) return;
    const w = els.canvasWrap.clientWidth || 800, h = WAVE_H;
    const g = WaveView.fit(els.canvas, w, h);
    const s = src.settings;
    const css = getComputedStyle(root);
    const cInk = css.getPropertyValue('--vs-wave').trim() || '#e8edf1';
    const cPeak = css.getPropertyValue('--vs-wave-peak').trim() || '#7f8c99';
    const cDim = css.getPropertyValue('--vs-wave-dim').trim() || '#3a4450';
    const cDimPeak = css.getPropertyValue('--vs-wave-dim-peak').trim() || '#2a313a';
    const cSel = css.getPropertyValue('--vs-sel').trim() || 'rgba(255,255,255,0.06)';
    g.clearRect(0, 0, w, h);
    // background + selection band
    g.fillStyle = 'rgba(0,0,0,0.35)'; g.fillRect(0, 0, w, h);
    const xi = xOf(s.inFrame, w), xo = xOf(s.outFrame, w);
    g.fillStyle = cSel; g.fillRect(Math.max(0, xi), 0, Math.min(w, xo) - Math.max(0, xi), h);
    // centre line
    g.fillStyle = 'rgba(255,255,255,0.08)'; g.fillRect(0, h / 2, w, 1);
    // Drawn at the level the export will have, so a quiet file is not a
    // flat line on screen and then loud on the card.
    const scale = WaveView.displayScale(src.buffer, src.mip, s.inFrame, s.outFrame, s);
    WaveView.drawFilled(g, src.buffer, src.mip, 0, 8, w, h - 16, view.f0, view.f1, cDimPeak, null, cDim, scale);
    const a = Math.max(view.f0, s.inFrame), b = Math.min(view.f1, s.outFrame);
    if (b > a) {
      const xa = xOf(a, w), xb = xOf(b, w);
      g.save(); g.beginPath(); g.rect(xa, 0, xb - xa, h); g.clip();
      WaveView.drawFilled(g, src.buffer, src.mip, 0, 8, w, h - 16, view.f0, view.f1, cPeak, null, cInk, scale);
      g.restore();
    }
    // fades as curves over the selection ends
    const fi = s.fadeInMs * RATE / 1000, fo = s.fadeOutMs * RATE / 1000;
    g.strokeStyle = 'rgba(255,200,90,0.9)'; g.lineWidth = 1.5;
    if (fi > 0) curve(g, xOf(s.inFrame, w), xOf(s.inFrame + fi, w), h, true);
    if (fo > 0) curve(g, xOf(s.outFrame - fo, w), xOf(s.outFrame, w), h, false);
    // handles
    g.fillStyle = '#ffffff';
    g.fillRect(xi - 1, 0, 2, h); g.fillRect(xo - 1, 0, 2, h);
    g.fillRect(xi - HANDLE_PX, 0, HANDLE_PX, 14); g.fillRect(xo, 0, HANDLE_PX, 14);
    g.fillRect(xi - HANDLE_PX, h - 14, HANDLE_PX, 14); g.fillRect(xo, h - 14, HANDLE_PX, 14);
    // playhead
    const xp = xOf(playhead, w);
    g.fillStyle = '#ff5c4d'; g.fillRect(xp - 0.5, 0, 1.5, h);
    // time scale
    const pxPerSec = w / ((view.f1 - view.f0) / RATE);
    const step = WaveView.tickStep(pxPerSec);
    g.fillStyle = 'rgba(255,255,255,0.45)'; g.font = '10px ' + (css.getPropertyValue('--vs-font-body') || 'system-ui');
    const t0 = Math.floor(view.f0 / RATE / step) * step;
    for (let t = t0; t * RATE <= view.f1; t += step) {
      const x = xOf(t * RATE, w); if (x < 0) continue;
      g.fillRect(x, h - 4, 1, 4); g.fillText(WaveView.timeLabel(t, step), x + 3, h - 6);
    }
    els.time.textContent = `${fmt(playhead, true)} / sel ${fmt(s.outFrame - s.inFrame, true)}`;
  }
  function curve(g, x0, x1, h, rising) {
    g.beginPath();
    const n = 24;
    for (let i = 0; i <= n; i++) {
      const t = i / n, v = 0.5 - 0.5 * Math.cos(Math.PI * (rising ? t : 1 - t));
      const x = x0 + (x1 - x0) * t, y = h - 2 - v * (h - 4);
      if (i === 0) g.moveTo(x, y); else g.lineTo(x, y);
    }
    g.stroke();
  }

  // ---- pointer + wheel -----------------------------------------------------
  function bindCanvas() {
    const cv = els.canvas;
    cv.addEventListener('pointerdown', (e) => {
      if (!src) return;
      const r = cv.getBoundingClientRect(), w = r.width, x = e.clientX - r.left;
      const s = src.settings;
      const xi = xOf(s.inFrame, w), xo = xOf(s.outFrame, w);
      cv.setPointerCapture(e.pointerId);
      if (Math.abs(x - xi) <= HANDLE_PX + 2) drag = { kind: 'in' };
      else if (Math.abs(x - xo) <= HANDLE_PX + 2) drag = { kind: 'out' };
      else drag = { kind: 'select', x0: x, f0: fOf(x, w), moved: false };
      e.preventDefault();
    });
    cv.addEventListener('pointermove', (e) => {
      if (!drag || !src) return;
      const r = cv.getBoundingClientRect(), w = r.width, x = Math.max(0, Math.min(w, e.clientX - r.left));
      const f = fOf(x, w), s = src.settings;
      if (drag.kind === 'in') { s.inFrame = Math.max(0, Math.min(s.outFrame - 1, Math.round(f))); }
      else if (drag.kind === 'out') { s.outFrame = Math.min(src.buffer.length, Math.max(s.inFrame + 1, Math.round(f))); }
      else if (Math.abs(x - drag.x0) > 3 || drag.moved) {
        drag.moved = true;
        const a = Math.min(drag.f0, f), b = Math.max(drag.f0, f);
        s.inFrame = Math.max(0, Math.round(a)); s.outFrame = Math.min(src.buffer.length, Math.max(s.inFrame + 1, Math.round(b)));
      }
      syncControls(); render();
    });
    const up = (e) => {
      if (!drag || !src) return;
      const s = src.settings;
      if (drag.kind === 'in') { s.inFrame = snap(s.inFrame); if (s.inFrame >= s.outFrame) s.inFrame = s.outFrame - 1; api.commit('trim'); }
      else if (drag.kind === 'out') { s.outFrame = snap(s.outFrame); if (s.outFrame <= s.inFrame) s.outFrame = s.inFrame + 1; api.commit('trim'); }
      else if (drag.moved) { s.inFrame = snap(s.inFrame); s.outFrame = snap(s.outFrame); if (s.outFrame <= s.inFrame) s.outFrame = Math.min(src.buffer.length, s.inFrame + 1); api.commit('select'); }
      else playhead = Math.round(drag.f0);
      if (player) restartPlayback();   // a new selection or playhead takes effect at once
      drag = null; syncControls(); render();
    };
    cv.addEventListener('pointerup', up); cv.addEventListener('pointercancel', up);
    cv.addEventListener('wheel', (e) => {
      if (!src) return;
      e.preventDefault();
      const r = cv.getBoundingClientRect(), w = r.width, x = e.clientX - r.left;
      const span = view.f1 - view.f0;
      if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        const d = (e.deltaX || e.deltaY) / w * span;
        let f0 = view.f0 + d; f0 = Math.max(0, Math.min(src.buffer.length - span, f0));
        view = { f0, f1: f0 + span }; render();
      } else {
        const k = Math.exp(e.deltaY * 0.0025);
        const fc = fOf(x, w);
        let ns = Math.min(src.buffer.length, Math.max(64, span * k));
        let f0 = fc - (x / w) * ns; f0 = Math.max(0, Math.min(src.buffer.length - ns, f0));
        view = { f0, f1: f0 + ns }; render();
      }
    }, { passive: false });
    cv.addEventListener('dblclick', () => { if (src) setView(0, src.buffer.length); });
  }

  // ---- playback ------------------------------------------------------------
  function togglePlay(loop) {
    if (!src) return;
    if (player && player.loop === loop) { stop(); return; }
    startPlayback(loop);
  }
  function startPlayback(loop) {
    stop();
    const s = src.settings;
    const seg = Audio.previewBuffer(src.buffer, s);
    let offset = 0;
    if (!loop && playhead > s.inFrame && playhead < s.outFrame - RATE * 0.05) offset = (playhead - s.inFrame) / RATE;
    const p = Audio.playSegment(seg, { loop, offset });
    player = { p, loop };
    els.play.classList.toggle('vs-active', !loop); els.loop.classList.toggle('vs-active', loop);
    const tick = () => {
      if (!player) return;
      const pos = p.position();
      if (pos === null) { stop(); return; }
      playhead = s.inFrame + Math.round(pos * RATE);
      render(); raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    p.done.then(() => { if (player && player.p === p) { player = null; els.play.classList.remove('vs-active'); els.loop.classList.remove('vs-active'); playhead = loop ? playhead : s.outFrame; render(); } });
  }
  function restartPlayback() { const loop = player.loop; startPlayback(loop); }
  function stop() {
    if (player) { player.p.stop(); player = null; }
    cancelAnimationFrame(raf);
    if (els.play) { els.play.classList.remove('vs-active'); els.loop.classList.remove('vs-active'); }
  }

  // Keys the app forwards when this view is active. Returns true if used.
  function key(e) {
    if (!src) return false;
    switch (e.key) {
      case ' ': togglePlay(false); return true;
      case 'l': case 'L': togglePlay(true); return true;
      case 'i': case 'I': setIn(playhead); return true;
      case 'o': case 'O': setOut(playhead); return true;
      case 'Home': playhead = src.settings.inFrame; render(); return true;
      case 'End': playhead = src.settings.outFrame; render(); return true;
      case 'Escape': stop(); return true;
      default: return false;
    }
  }

  return { mount, show, render, stop, key, syncControls, get current() { return src; } };
})();

// ---- arrange.js ------------------------------------------------------------
// WAV Player - Sample Editor: the timeline. Tracks with gain, mute and solo; clips
// dragged in from the library, moved, trimmed at the edges, split at the
// playhead; a loop region; and a mix down that becomes a new library item.
// No tempo grid: clips snap to each other, to zero and to the playhead.
//
// Playback is honest by construction: pressing Play mixes the region to a
// buffer with the same code the mix down uses, then plays that buffer. What
// you hear is the file you would get.
const Arrange = (() => {
  const RATE = Rules.DEVICE_RATE;
  const tip = Tip.set;
  const RULER_H = 26, TRACK_H = 80, EDGE_PX = 7, SNAP_PX = 8;
  const LABEL_H = 16, PAD = 3;
  // One colour per track, the way every DAW does it, so a clip says which
  // lane it belongs to even when dragged.
  const TRACK_COLORS = [[90, 169, 230], [244, 162, 97], [139, 209, 124], [224, 122, 154], [201, 180, 88], [124, 199, 193], [180, 140, 230], [230, 120, 100]];
  const rgba = (c, a) => `rgba(${c[0]},${c[1]},${c[2]},${a})`;
  const trackColor = (i) => TRACK_COLORS[i % TRACK_COLORS.length];
  const MIN_PPS = 3, MAX_PPS = 3000;
  // Widest scroll content a browser lays out reliably; longer timelines map onto it.
  const MAX_BAR_PX = 10000000;

  let api, root, els = {};
  let pps = 60, scroll = 0;          // pixels per second, seconds scrolled
  let playhead = 0, sel = null, drag = null, player = null, raf = 0, snapLine = null;
  let lastMixInfo = null;
  let lastBar = null, barLeft = 0;     // horizontal scrollbar state (see syncBar)

  const el = (tag, cls, html) => { const e = document.createElement(tag); if (cls) e.className = cls; if (html !== undefined) e.innerHTML = html; return e; };
  const btn = (label, cls, fn) => { const b = el('button', cls, label); b.type = 'button'; b.addEventListener('click', fn); return b; };
  const proj = () => api.project();
  const src = (id) => api.sources().get(id);
  const clipDur = (c) => (c.outFrame - c.inFrame) / RATE;
  const clipEnd = (c) => c.at + clipDur(c);
  const uid = () => Math.random().toString(36).slice(2, 10);

  function mount(container, appApi) {
    api = appApi; root = container; root.innerHTML = '';
    const tb = el('div', 'vs-row vs-transport');
    els.play = tip(btn('Play', 'vs-btn vs-btn-primary', () => togglePlay()), 'Play every track together from the red line. The space bar also plays and stops.');
    els.stop = tip(btn('Stop', 'vs-btn', stop), 'Stop playing.');
    els.loopBtn = tip(btn('Loop region', 'vs-btn', () => { const p = proj(); if (p.loop) { p.loop = null; } else { p.loop = { a: 0, b: Math.max(1, projectEnd()) }; } api.commit('loop'); render(); restartIfPlaying(); }),
      'Play one section over and over. Drag across the ruler at the top to choose the section, or click here to loop everything.');
    els.time = el('span', 'vs-time', '0:00.00');
    els.zoomOut = tip(btn('−', 'vs-btn vs-btn-sm', () => zoomBy(0.7)), 'Zoom out.');
    els.zoomIn = tip(btn('+', 'vs-btn vs-btn-sm', () => zoomBy(1 / 0.7)), 'Zoom in.');
    els.fit = tip(btn('Fit', 'vs-btn vs-btn-sm', fit), 'Zoom to show the whole project.');
    els.addTrack = tip(btn('+ Track', 'vs-btn vs-btn-sm', () => { addTrack(); api.commit('add track'); render(); }), 'Add another track. Sounds on different tracks play at the same time.');
    els.split = tip(btn('Split (S)', 'vs-btn vs-btn-sm', split), 'Cut the highlighted clip in two at the red line. The S key does the same.');
    els.mix = tip(btn('Mix down', 'vs-btn vs-btn-primary', mixdown), 'Combine every track into one new sound and add it to the library, ready to name and save.');
    els.help = Tip.chip('Drag a sound from the Library onto a track. Drag a clip to move it, drag its ends to trim it, double-click it to open it in Samples. Drag across the ruler to set a loop. Scroll to zoom; use the bar under the tracks, or hold Shift and scroll, to move along. Delete removes the highlighted clip.');
    tb.append(els.play, els.stop, els.loopBtn, els.time, el('span', 'vs-spacer'), els.zoomOut, els.zoomIn, els.fit, els.addTrack, els.split, els.mix, els.help);

    els.body = el('div', 'vs-arr');
    els.heads = el('div', 'vs-arr-heads');
    els.canvasWrap = el('div', 'vs-arr-canvas-wrap');
    els.canvas = el('canvas', 'vs-arr-canvas');
    // A real scrollbar under the lanes: the only other way along was Shift+wheel.
    els.hbar = el('div', 'vs-arr-hbar');
    els.hbarFill = el('div', 'vs-arr-hbar-fill');
    els.hbar.append(els.hbarFill);
    els.canvasWrap.append(els.canvas, els.hbar);
    els.body.append(els.heads, els.canvasWrap);
    els.readout = tip(el('div', 'vs-readout', ''), "How long the finished mix will be and how much of one preset's memory it takes. Amber means the tracks add up louder than a file can hold somewhere: lower a track, or leave Normalize on when you save the mix.");
    root.append(tb, els.body, els.readout);
    bind();
    window.addEventListener('resize', () => { if (!root.hidden) render(); });
  }

  // ---- model helpers -------------------------------------------------------
  function addTrack(name) {
    const p = proj();
    const t = { id: uid(), name: name || `Track ${p.tracks.length + 1}`, gainDb: 0, mute: false, solo: false };
    p.tracks.push(t); return t;
  }
  function ensureTracks() { const p = proj(); while (p.tracks.length < 2) addTrack(); }
  function projectEnd() { let e = 0; for (const c of proj().clips) e = Math.max(e, clipEnd(c)); return e; }
  function trackIndex(id) { return proj().tracks.findIndex((t) => t.id === id); }
  function clipAt(x, y) {
    const p = proj();
    const ti = Math.floor((y - RULER_H) / TRACK_H);
    if (ti < 0 || ti >= p.tracks.length) return null;
    const t = p.tracks[ti]; const sec = xToSec(x);
    // topmost = last drawn = last in array
    for (let i = p.clips.length - 1; i >= 0; i--) {
      const c = p.clips[i];
      if (c.trackId === t.id && sec >= c.at && sec <= clipEnd(c)) return c;
    }
    return null;
  }
  function addClipFromSource(s, trackId, at) {
    const st = s.settings;
    const c = { id: uid(), sourceId: s.id, trackId, at: Math.max(0, at), inFrame: st.inFrame, outFrame: st.outFrame,
      gainDb: st.gainDb || 0, fadeInMs: st.fadeInMs || 0, fadeOutMs: st.fadeOutMs || 0 };
    proj().clips.push(c); return c;
  }

  // ---- geometry --------------------------------------------------------------
  const secToX = (sec) => (sec - scroll) * pps;
  const xToSec = (x) => scroll + x / pps;
  function canvasW() { return els.canvasWrap.clientWidth || 800; }
  function canvasH() { return RULER_H + Math.max(2, proj().tracks.length) * TRACK_H; }
  // Zoom floor: the usual 3 px/s, or lower when that is what fitting the whole project takes.
  function zoomFloor(viewPx, projectSec) { return Math.min(MIN_PPS, Math.max(1, viewPx - 40) / Math.max(1, projectSec)); }
  // Scrollbar geometry. The view can travel from 0 to rangeSec; that range maps
  // linearly onto the bar's travel, so both ends are exact even when a very long
  // timeline is squeezed into MAX_BAR_PX. A little room is kept after the last clip.
  function scrollbar(scrollSec, pps, viewPx, projectSec) {
    const viewSec = viewPx / pps;
    const endSec = Math.max(projectSec + viewSec * 0.1, scrollSec + viewSec);
    const rangeSec = Math.max(0, endSec - viewSec);
    const contentPx = Math.min(MAX_BAR_PX, Math.max(viewPx, Math.round(endSec * pps)));
    const travel = contentPx - viewPx;
    return { contentPx, rangeSec, leftPx: travel > 0 && rangeSec > 0 ? scrollSec / rangeSec * travel : 0 };
  }
  function scrollFromBar(leftPx, pps, viewPx, bar) {
    const travel = bar.contentPx - viewPx;
    return travel > 0 ? leftPx / travel * bar.rangeSec : 0;
  }
  function syncBar(w) {
    lastBar = scrollbar(scroll, pps, w, projectEnd());
    els.hbarFill.style.width = lastBar.contentPx + 'px';
    els.hbar.scrollLeft = lastBar.leftPx;
    barLeft = els.hbar.scrollLeft;     // read back: the browser rounds it
  }

  function zoomBy(k, xAnchor) {
    const w = canvasW(); if (xAnchor === undefined) xAnchor = w / 2;
    const secAt = xToSec(xAnchor);
    pps = Math.min(MAX_PPS, Math.max(zoomFloor(w, projectEnd()), pps * k));
    scroll = Math.max(0, secAt - xAnchor / pps);
    render();
  }
  function fit() { const w = canvasW(), end = Math.max(1, projectEnd()); pps = Math.min(MAX_PPS, Math.max(zoomFloor(w, end), (w - 40) / end)); scroll = 0; render(); }

  // Snap a candidate time to clip edges, zero and the playhead.
  function snapTime(sec, ignoreClipId) {
    const pts = [0, playhead];
    for (const c of proj().clips) if (c.id !== ignoreClipId) pts.push(c.at, clipEnd(c));
    if (proj().loop) pts.push(proj().loop.a, proj().loop.b);
    let best = sec, bestD = SNAP_PX / pps;
    for (const p of pts) { const d = Math.abs(p - sec); if (d < bestD) { bestD = d; best = p; } }
    snapLine = best !== sec ? best : null;
    return Math.max(0, best);
  }

  // ---- render ----------------------------------------------------------------
  function render() {
    if (root.hidden) return;
    ensureTracks();
    renderHeads();
    const w = canvasW(), h = canvasH();
    syncBar(w);
    const g = WaveView.fit(els.canvas, w, h);
    const p = proj(), css = getComputedStyle(root);
    const font = css.getPropertyValue('--vs-font-body') || 'system-ui';
    g.clearRect(0, 0, w, h);
    // lanes
    g.fillStyle = '#0b0d10'; g.fillRect(0, RULER_H, w, h - RULER_H);
    for (let i = 0; i < p.tracks.length; i++) {
      const y = RULER_H + i * TRACK_H;
      if (i % 2) { g.fillStyle = 'rgba(255,255,255,0.02)'; g.fillRect(0, y, w, TRACK_H); }
      g.fillStyle = 'rgba(255,255,255,0.09)'; g.fillRect(0, y + TRACK_H - 1, w, 1);
    }
    // time grid through the lanes, from the same ticks as the ruler
    const step = WaveView.tickStep(pps);
    for (let t = Math.floor(scroll / step) * step; secToX(t) < w; t += step) {
      const x = Math.round(secToX(t)) + 0.5;
      if (x >= 0) { g.fillStyle = 'rgba(255,255,255,0.10)'; g.fillRect(x, RULER_H, 1, h - RULER_H); }
      for (let k = 1; k < 4; k++) { const xs = Math.round(secToX(t + step * k / 4)) + 0.5; if (xs >= 0 && xs < w) { g.fillStyle = 'rgba(255,255,255,0.035)'; g.fillRect(xs, RULER_H, 1, h - RULER_H); } }
    }
    // loop region
    if (p.loop) {
      const xa = secToX(p.loop.a), xb = secToX(p.loop.b);
      g.fillStyle = 'rgba(255,200,90,0.07)'; g.fillRect(xa, RULER_H, xb - xa, h - RULER_H);
      g.fillStyle = 'rgba(255,200,90,0.55)'; g.fillRect(xa, 0, xb - xa, RULER_H);
    }
    // ruler
    g.fillStyle = 'rgba(0,0,0,0.5)'; g.fillRect(0, 0, w, RULER_H);
    g.fillStyle = 'rgba(255,255,255,0.14)'; g.fillRect(0, RULER_H - 1, w, 1);
    g.font = '10px ' + font; g.fillStyle = 'rgba(255,255,255,0.6)';
    for (let t = Math.floor(scroll / step) * step; secToX(t) < w; t += step) {
      const x = Math.round(secToX(t)); if (x < 0) continue;
      g.fillRect(x, RULER_H - 8, 1, 8); g.fillText(WaveView.timeLabel(t, step), x + 4, 11);
      for (let k = 1; k < 4; k++) { const xs = Math.round(secToX(t + step * k / 4)); g.fillRect(xs, RULER_H - 4, 1, 4); }
    }
    // clips
    const soloed = p.tracks.some((t) => t.solo);
    for (const c of p.clips) {
      const ti = trackIndex(c.trackId); if (ti < 0) continue;
      const t = p.tracks[ti];
      const s = src(c.sourceId); if (!s) continue;
      const x = Math.round(secToX(c.at)), cw = Math.max(2, Math.round(clipDur(c) * pps));
      const y = RULER_H + ti * TRACK_H + PAD, ch = TRACK_H - 2 * PAD - 1;
      if (x + cw < 0 || x > w) continue;
      const silent = t.mute || (soloed && !t.solo);
      const selected = sel === c.id;
      const col = silent ? [150, 158, 166] : trackColor(ti);
      g.save();
      roundRect(g, x, y, cw, ch, 4); g.clip();
      // body
      g.fillStyle = rgba(col, selected ? 0.30 : 0.20); g.fillRect(x, y, cw, ch);
      // waveform, contained: inside the body with padding, below the name strip
      const wy = y + LABEL_H + PAD, wh = ch - LABEL_H - 2 * PAD;
      const vx0 = Math.max(x + 1, 0), vx1 = Math.min(x + cw - 1, w);
      if (vx1 > vx0 && wh > 4) {
        const f0 = c.inFrame + (vx0 - x) / pps * RATE, f1 = c.inFrame + (vx1 - x) / pps * RATE;
        WaveView.drawFilled(g, s.buffer, s.mip, vx0, wy, vx1 - vx0, wh, f0, f1, rgba(col, silent ? 0.35 : 0.55), rgba(col, 0.35), rgba(col, silent ? 0.6 : 1));
      }
      // fades: darken the faded region and draw the curve
      const fi = c.fadeInMs / 1000 * pps, fo = c.fadeOutMs / 1000 * pps;
      if (fi > 0) { g.fillStyle = 'rgba(0,0,0,0.35)'; g.beginPath(); g.moveTo(x, y + LABEL_H); g.lineTo(x + fi, y + LABEL_H); g.lineTo(x, y + ch); g.closePath(); g.fill(); }
      if (fo > 0) { g.fillStyle = 'rgba(0,0,0,0.35)'; g.beginPath(); g.moveTo(x + cw, y + LABEL_H); g.lineTo(x + cw - fo, y + LABEL_H); g.lineTo(x + cw, y + ch); g.closePath(); g.fill(); }
      // name strip
      g.fillStyle = rgba(col, silent ? 0.35 : 0.85); g.fillRect(x, y, cw, LABEL_H);
      g.fillStyle = silent ? 'rgba(255,255,255,0.6)' : '#0b0d10'; g.font = '600 10.5px ' + font;
      let label = s.name + (c.gainDb ? ` ${c.gainDb > 0 ? '+' : ''}${c.gainDb} dB` : '');
      const maxW = cw - 9;
      if (g.measureText(label).width > maxW) {   // cut with an ellipsis, never squeeze the glyphs
        while (label.length > 1 && g.measureText(label + '…').width > maxW) label = label.slice(0, -1);
        label += '…';
      }
      if (maxW > 6) g.fillText(label, Math.max(x, 0) + 5, y + 11.5);
      g.restore();
      g.strokeStyle = selected ? '#ffffff' : rgba(col, 0.75); g.lineWidth = selected ? 1.5 : 1;
      roundRect(g, x + 0.5, y + 0.5, cw - 1, ch - 1, 4); g.stroke();
    }
    // snap guide
    if (snapLine !== null) { g.fillStyle = 'rgba(255,200,90,0.8)'; g.fillRect(Math.round(secToX(snapLine)), 0, 1, h); }
    // playhead
    const px = Math.round(secToX(playhead));
    g.fillStyle = '#ff5c4d'; g.fillRect(px, 0, 1, h);
    g.beginPath(); g.moveTo(px - 5, 0); g.lineTo(px + 6, 0); g.lineTo(px + 0.5, 7); g.closePath(); g.fill();
    // readout
    const end = projectEnd();
    const bytes = Rules.bytesForFrames(Math.round(end * RATE));
    let line = `${p.clips.length} clip${p.clips.length === 1 ? '' : 's'}, ${Rules.formatDuration(end, true)} long, ${Rules.formatBytes(bytes)} at 48 kHz (${((bytes / Rules.POOL_BYTES) * 100).toFixed(1)}% of a preset)`;
    if (lastMixInfo && lastMixInfo.clipped) line += `. ${lastMixInfo.clipped} samples clip: lower a track or turn normalize on when exporting.`;
    if (bytes > Rules.POOL_BYTES) line += '. Too long to hold in memory, so it plays straight from the card.';
    els.readout.textContent = line; els.readout.className = 'vs-readout ' + (bytes > Rules.POOL_BYTES || (lastMixInfo && lastMixInfo.clipped) ? 'vs-warn' : '');
    els.time.textContent = Rules.formatDuration(playhead, true);
    els.loopBtn.classList.toggle('vs-active', !!p.loop);
    els.mix.disabled = p.clips.length === 0;
  }
  function roundRect(g, x, y, w, h, r) {
    r = Math.min(r, w / 2, h / 2);
    g.beginPath(); g.moveTo(x + r, y); g.arcTo(x + w, y, x + w, y + h, r); g.arcTo(x + w, y + h, x, y + h, r);
    g.arcTo(x, y + h, x, y, r); g.arcTo(x, y, x + w, y, r); g.closePath();
  }

  function renderHeads() {
    const p = proj();
    els.heads.innerHTML = '';
    const spacer = el('div', 'vs-arr-ruler-spacer'); spacer.style.height = RULER_H + 'px';
    els.heads.append(spacer);
    const soloed = p.tracks.some((t) => t.solo);
    p.tracks.forEach((t, i) => {
      const col = trackColor(i);
      const h = el('div', 'vs-track-head' + ((t.mute || (soloed && !t.solo)) ? ' vs-silent' : ''));
      h.style.height = TRACK_H + 'px';
      h.style.setProperty('--vs-track', rgba(col, 1));
      const top = el('div', 'vs-track-row');
      const name = tip(el('input', 'vs-track-name'), 'Track name. Click to change it.'); name.value = t.name; name.spellcheck = false;
      name.addEventListener('change', () => { t.name = name.value || `Track ${i + 1}`; api.commit('track name'); });
      name.addEventListener('keydown', (e) => { e.stopPropagation(); if (e.key === 'Enter') name.blur(); });
      const x = btn('✕', 'vs-btn vs-btn-xs vs-btn-ghost vs-btn-danger', () => {
        if (p.tracks.length <= 1) return;
        const n = p.clips.filter((c) => c.trackId === t.id).length;
        if (n && !confirm(`Remove ${t.name} and its ${n} clip${n === 1 ? '' : 's'}?`)) return;
        p.clips = p.clips.filter((c) => c.trackId !== t.id); p.tracks.splice(i, 1);
        api.commit('remove track'); render();
      });
      tip(x, 'Remove this track and any clips on it.');
      top.append(name, x);
      const row = el('div', 'vs-track-row');
      const m = btn('M', 'vs-btn vs-btn-xs vs-btn-track' + (t.mute ? ' vs-on-mute' : ''), () => { t.mute = !t.mute; api.commit('mute'); render(); restartIfPlaying(); });
      const so = btn('S', 'vs-btn vs-btn-xs vs-btn-track' + (t.solo ? ' vs-on-solo' : ''), () => { t.solo = !t.solo; api.commit('solo'); render(); restartIfPlaying(); });
      tip(m, 'Mute. Silences this track.'); tip(so, 'Solo. Plays only this track.');
      const gain = tip(el('input', 'vs-track-gain'), 'Volume of this whole track. Double-click to reset.'); gain.type = 'range'; gain.min = -40; gain.max = 6; gain.step = 0.5; gain.value = t.gainDb;
      const gv = el('span', 'vs-track-db', fmtDb(t.gainDb));
      gain.addEventListener('input', () => { gv.textContent = fmtDb(+gain.value); });
      gain.addEventListener('change', () => { t.gainDb = +gain.value; api.commit('track gain'); restartIfPlaying(); });
      gain.addEventListener('dblclick', () => { gain.value = 0; t.gainDb = 0; gv.textContent = fmtDb(0); api.commit('track gain'); restartIfPlaying(); });
      row.append(m, so, gain, gv);
      h.append(top, row);
      els.heads.append(h);
    });
  }
  const fmtDb = (v) => (v > 0 ? '+' : '') + v.toFixed(1) + ' dB';

  // ---- interaction -----------------------------------------------------------
  function bind() {
    const cv = els.canvas;
    cv.addEventListener('pointerdown', (e) => {
      const r = cv.getBoundingClientRect(), x = e.clientX - r.left, y = e.clientY - r.top;
      cv.setPointerCapture(e.pointerId);
      if (y < RULER_H) { drag = { kind: 'ruler', x0: x, sec0: xToSec(x), moved: false }; return; }
      const c = clipAt(x, y);
      if (c) {
        sel = c.id;
        const cx0 = secToX(c.at), cx1 = secToX(clipEnd(c));
        if (x - cx0 <= EDGE_PX) drag = { kind: 'trimL', c, orig: { ...c } };
        else if (cx1 - x <= EDGE_PX) drag = { kind: 'trimR', c, orig: { ...c } };
        else drag = { kind: 'move', c, orig: { ...c }, dx: x - cx0, y0: y, moved: false };
      } else { sel = null; drag = { kind: 'seek', x0: x, moved: false }; }
      render();
    });
    cv.addEventListener('pointermove', (e) => {
      const r = cv.getBoundingClientRect(), x = e.clientX - r.left, y = e.clientY - r.top;
      if (!drag) { hoverCursor(x, y); return; }
      const p = proj();
      if (drag.kind === 'ruler') {
        const a = Math.min(drag.sec0, xToSec(x)), b = Math.max(drag.sec0, xToSec(x));
        if (Math.abs(x - drag.x0) > 3) { drag.moved = true; p.loop = { a: Math.max(0, a), b }; }
      } else if (drag.kind === 'move') {
        drag.moved = true;
        const c = drag.c;
        c.at = snapTime(xToSec(x - drag.dx), c.id);
        const ti = Math.max(0, Math.min(p.tracks.length - 1, Math.floor((y - RULER_H) / TRACK_H)));
        c.trackId = p.tracks[ti].id;
      } else if (drag.kind === 'trimL') {
        const c = drag.c, o = drag.orig;
        let at = snapTime(xToSec(x), c.id);
        const maxAt = o.at + (o.outFrame - o.inFrame - 48) / RATE;
        at = Math.min(maxAt, Math.max(o.at - o.inFrame / RATE, at));
        c.inFrame = Math.round(o.inFrame + (at - o.at) * RATE); c.at = at;
      } else if (drag.kind === 'trimR') {
        const c = drag.c, o = drag.orig, s = src(c.sourceId);
        let end = snapTime(xToSec(x), c.id);
        const minEnd = o.at + 48 / RATE, maxEnd = o.at + (s.buffer.length - o.inFrame) / RATE;
        end = Math.max(minEnd, Math.min(maxEnd, end));
        c.outFrame = Math.round(o.inFrame + (end - o.at) * RATE);
      } else if (drag.kind === 'seek') {
        if (Math.abs(x - drag.x0) > 3) drag.moved = true;
      }
      render();
    });
    const up = (e) => {
      if (!drag) return;
      const r = cv.getBoundingClientRect(), x = e.clientX - r.left;
      // A new loop region takes effect at once if the mix is playing
      if (drag.kind === 'ruler') { if (!drag.moved) { playhead = Math.max(0, xToSec(x)); } else api.commit('loop region'); restartIfPlaying(); }
      else if (drag.kind === 'seek') { if (!drag.moved) { playhead = Math.max(0, xToSec(x)); restartIfPlaying(); } }
      else if (drag.kind === 'move') { if (drag.moved) api.commit('move clip'); }
      else api.commit('trim clip');
      drag = null; snapLine = null; render();
    };
    cv.addEventListener('pointerup', up); cv.addEventListener('pointercancel', up);
    cv.addEventListener('dblclick', (e) => {
      const r = cv.getBoundingClientRect(); const c = clipAt(e.clientX - r.left, e.clientY - r.top);
      if (c) api.selectSource(c.sourceId, true);
    });
    // Dragging the scrollbar moves the view. Our own syncBar writes are skipped.
    els.hbar.addEventListener('scroll', () => {
      if (!lastBar || Math.abs(els.hbar.scrollLeft - barLeft) < 1) return;
      scroll = Math.max(0, scrollFromBar(els.hbar.scrollLeft, pps, canvasW(), lastBar));
      render();
    });
    cv.addEventListener('wheel', (e) => {
      e.preventDefault();
      const r = cv.getBoundingClientRect(), x = e.clientX - r.left;
      if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) { scroll = Math.max(0, scroll + (e.deltaX || e.deltaY) / pps); render(); }
      else zoomBy(Math.exp(-e.deltaY * 0.0025), x);
    }, { passive: false });
    // Drop from the library
    cv.addEventListener('dragover', (e) => { if ([...e.dataTransfer.types].includes('text/vs-source')) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; } });
    cv.addEventListener('drop', (e) => {
      const id = e.dataTransfer.getData('text/vs-source'); if (!id) return;
      e.preventDefault(); e.stopPropagation();
      const s = src(id); if (!s) return;
      const r = cv.getBoundingClientRect(), x = e.clientX - r.left, y = e.clientY - r.top;
      const p = proj(); ensureTracks();
      const ti = Math.max(0, Math.min(p.tracks.length - 1, Math.floor((y - RULER_H) / TRACK_H)));
      const c = addClipFromSource(s, p.tracks[ti].id, snapTime(xToSec(x), null));
      snapLine = null; sel = c.id; api.commit('add clip'); render();
    });
  }
  function hoverCursor(x, y) {
    const c = y >= RULER_H ? clipAt(x, y) : null;
    let cur = y < RULER_H ? 'col-resize' : 'default';
    if (c) { const cx0 = secToX(c.at), cx1 = secToX(clipEnd(c)); cur = (x - cx0 <= EDGE_PX || cx1 - x <= EDGE_PX) ? 'ew-resize' : 'grab'; }
    els.canvas.style.cursor = cur;
  }

  function split() {
    const p = proj();
    const c = p.clips.find((k) => k.id === sel && playhead > k.at && playhead < clipEnd(k))
      || p.clips.find((k) => playhead > k.at && playhead < clipEnd(k));
    if (!c) { api.toast('Put the playhead inside a clip to split it.'); return; }
    const cut = c.inFrame + Math.round((playhead - c.at) * RATE);
    const right = { ...c, id: uid(), at: playhead, inFrame: cut, fadeInMs: 0 };
    c.outFrame = cut; c.fadeOutMs = 0;
    p.clips.push(right); sel = right.id;
    api.commit('split'); render();
  }
  function deleteSelected() {
    const p = proj(); if (!sel) return false;
    p.clips = p.clips.filter((c) => c.id !== sel); sel = null;
    api.commit('delete clip'); render(); return true;
  }

  // ---- mixing ----------------------------------------------------------------
  // Render [fromSec, toSec) of the project to one mono buffer.
  function mix(fromSec, toSec) {
    const p = proj(), soloed = p.tracks.some((t) => t.solo);
    const total = Math.max(1, Math.round((toSec - fromSec) * RATE));
    const parts = [];
    for (const c of p.clips) {
      const t = p.tracks.find((k) => k.id === c.trackId); const s = src(c.sourceId);
      if (!t || !s || t.mute || (soloed && !t.solo)) continue;
      const atF = Math.round((c.at - fromSec) * RATE);
      if (atF >= total || atF + (c.outFrame - c.inFrame) <= 0) continue;
      const buf = new Float32Array(s.buffer.subarray(c.inFrame, c.outFrame));
      Dsp.fade(buf, Math.round(c.fadeInMs * RATE / 1000), Math.round(c.fadeOutMs * RATE / 1000));
      parts.push({ buf, at: atF, gain: Dsp.dbToGain((c.gainDb || 0) + t.gainDb) });
    }
    // Clips that start before the window: shift them in
    for (const q of parts) if (q.at < 0) { q.buf = q.buf.subarray(-q.at); q.at = 0; }
    const out = Dsp.mix(parts, total);
    lastMixInfo = { clipped: Dsp.clippedCount(out), frames: total };
    return out;
  }

  function togglePlay() { if (player) stop(); else play(); }
  function play() {
    stop();
    const p = proj(); const end = projectEnd(); if (end <= 0) return;
    let a, b, loop = false;
    if (p.loop && p.loop.b > p.loop.a) { a = p.loop.a; b = p.loop.b; loop = true; if (playhead < a || playhead >= b) playhead = a; }
    else { a = Math.min(playhead, end); b = end; if (b - a < 0.01) { playhead = 0; a = 0; } }
    const buf = mix(a, b);
    const h = Audio.playSegment(buf, { loop, offset: playhead - a });
    player = { h, a, b, loop };
    els.play.classList.add('vs-active');
    const tick = () => { if (!player) return; const pos = h.position(); if (pos === null) { stop(); return; } playhead = a + pos; render(); raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    h.done.then(() => { if (player && player.h === h) { player = null; els.play.classList.remove('vs-active'); if (!loop) playhead = b; render(); } });
  }
  function restartIfPlaying() { if (player) play(); }
  function stop() { if (player) { player.h.stop(); player = null; } cancelAnimationFrame(raf); if (els.play) els.play.classList.remove('vs-active'); }

  async function mixdown() {
    const end = projectEnd(); if (end <= 0) return;
    const name = await api.askName('Name for the mixed file', 'MIX');
    if (!name) return;
    const buf = mix(0, end);
    if (lastMixInfo.clipped) api.toast(`${lastMixInfo.clipped} samples clip in this mix. Normalize is on by default in Samples, which brings the peak back under full scale.`, 'warn');
    api.addSource({ name, buffer: buf, kind: 'MIX' });
    render();
  }

  function key(e) {
    switch (e.key) {
      case ' ': togglePlay(); return true;
      case 's': case 'S': split(); return true;
      case 'Delete': case 'Backspace': return deleteSelected();
      case 'l': case 'L': els.loopBtn.click(); return true;
      case 'Home': playhead = 0; restartIfPlaying(); render(); return true;
      case 'Escape': stop(); sel = null; render(); return true;
      default: return false;
    }
  }

  return { mount, render, stop, key, fit, ensureTracks, mix, addClipFromSource, mixdown, projectEnd,
    view: { zoomFloor, scrollbar, scrollFromBar, MAX_BAR_PX } };
})();

// ---- app.js ----------------------------------------------------------------
// WAV Player - Sample Editor: the shell. Builds the page, owns the library and the
// project, routes keys, runs undo, talks to the card, and persists.
const App = (() => {
  const RATE = Rules.DEVICE_RATE;
  const tip = Tip.set;

  const state = {
    sources: new Map(),    // id -> source
    order: [],             // ids in library order
    selected: null,
    view: 'samples',
    mode: 'basic',         // 'basic' hides everything carrying .vs-adv
    hints: true,           // hover hints on or off
    project: { tracks: [], clips: [], loop: null },
    card: { items: [], budget: null },
    trash: new Map(),
    bin: 'lib',
    undo: [], redo: [],
    lastExport: null,      // for the test harness
    recording: null,
  };
  let root, els = {}, saveTimer = 0, savedSig = new Map();

  const el = (tag, cls, html) => { const e = document.createElement(tag); if (cls) e.className = cls; if (html !== undefined) e.innerHTML = html; return e; };
  const btn = (label, cls, fn) => { const b = el('button', cls, label); b.type = 'button'; b.addEventListener('click', fn); return b; };
  const uid = () => Math.random().toString(36).slice(2, 10);

  // ---- boot ------------------------------------------------------------------
  async function boot() {
    root = document.getElementById('vmod-studio');
    if (!root || root.dataset.booted) return;
    root.dataset.booted = '1';
    if (!Audio.supported) { root.innerHTML = '<p class="vs-empty">This browser has no Web Audio support, so the studio cannot run here.</p>'; return; }
    build();
    Tip.mount(root);
    Editor.mount(els.samplesView, api);
    Arrange.mount(els.arrangeView, api);
    bindGlobal();
    await restore();
    setMode(state.mode); setHints(state.hints);
    setView(state.view);
    renderLibrary(); renderCard();
    if (state.selected) Editor.show(state.sources.get(state.selected));
  }

  function build() {
    root.innerHTML = '';
    root.classList.add('vs-root');
    const shell = el('div', 'vs-shell');

    // Header grid: title top-left, card status top-right, view tabs
    // bottom-left, actions bottom-right. The two right-hand cells share one
    // column, so the card box and the button row line up on both edges.
    const head = el('header', 'vs-head');
    const brand = el('div', 'vs-brand');
    brand.append(el('h1', 'vs-title', 'Sample Editor'),
      el('p', 'vs-spec', 'Convert and arrange your audio files into samples for the VMod Pro WAV Player.'));
    els.cardBar = el('div', 'vs-cardbar');
    els.tabs = el('nav', 'vs-tabs vs-adv');
    els.tabSamples = tip(btn('Samples', 'vs-tab', () => setView('samples')), 'Work on one sound at a time: trim it, name it, save it.');
    els.tabArrange = tip(btn('Arrange', 'vs-tab', () => setView('arrange')), 'Place several sounds on tracks, line them up, and mix them into one file.');
    els.tabs.append(els.tabSamples, els.tabArrange);
    const acts = el('div', 'vs-actions');
    els.fileInput = el('input'); els.fileInput.type = 'file'; els.fileInput.multiple = true; els.fileInput.accept = 'audio/*,.wav,.mp3,.m4a,.aac,.flac,.ogg,.oga,.opus,.aif,.aiff,.caf'; els.fileInput.hidden = true;
    els.fileInput.addEventListener('change', () => { addFiles(els.fileInput.files); els.fileInput.value = ''; });
    els.addBtn = tip(btn('Add files', 'vs-btn vs-btn-primary', () => els.fileInput.click()), 'Pick audio files from your computer. MP3, M4A, AAC, FLAC, OGG, AIFF, and WAV at any quality all work.');
    els.recBtn = tip(btn('Record', 'vs-btn vs-rec', toggleRecord), 'Record from your microphone. Click again to stop. The recording goes into the library like any other file.');
    els.modeBasic = tip(btn('Basic', 'vs-seg-btn', () => setMode('basic')), 'Add a file, trim it, name it, save it. Everything else is set for you.');
    els.modeAdv = tip(btn('Advanced', 'vs-seg-btn', () => setMode('advanced')), 'Adds fades, volume, loop tools, keyboard shortcuts, and the Arrange view for mixing several sounds into one.');
    const seg = el('div', 'vs-seg'); seg.append(els.modeBasic, els.modeAdv);
    els.hintsBtn = tip(btn('Hints on', 'vs-btn vs-btn-sm vs-btn-ghost', () => setHints(!state.hints)), 'Turn these hover hints off or on. The ? buttons keep working either way.');
    acts.append(els.addBtn, els.recBtn, seg, els.hintsBtn, els.fileInput);
    head.append(brand, els.cardBar, els.tabs, acts);

    // Workspace: the editor or the timeline, full width
    const work = el('main', 'vs-work');
    els.samplesView = el('section', 'vs-view');
    els.arrangeView = el('section', 'vs-view'); els.arrangeView.hidden = true;
    work.append(els.samplesView, els.arrangeView);

    // Bin: library and card files as tables, the DAW's pool
    const bin = el('section', 'vs-bin');
    els.binTabs = el('nav', 'vs-tabs vs-tabs-sm');
    els.binLibTab = tip(btn('Library', 'vs-tab vs-active', () => setBin('lib')), 'Every sound you have added. Click a row to open it.');
    els.binCardTab = tip(btn('Card files', 'vs-tab', () => setBin('card')), 'What is on the card right now, and whether the VMod Pro can play each file.');
    els.binTabs.append(els.binLibTab, els.binCardTab);
    els.binLib = el('div', 'vs-bin-panel');
    els.binCard = el('div', 'vs-bin-panel'); els.binCard.hidden = true;
    els.drop = el('div', 'vs-drop', 'Drop audio files anywhere on the page, or use Add files.');
    els.libTable = el('div', 'vs-table-wrap');
    els.batch = el('div', 'vs-bin-foot vs-adv');
    els.batchAll = tip(btn('Select all', 'vs-btn vs-btn-sm vs-btn-ghost', () => { const all = state.order.every((id) => state.sources.get(id).checked); state.order.forEach((id) => { state.sources.get(id).checked = !all; }); renderLibrary(); }), 'Tick or untick every row.');
    els.batchBtn = tip(btn('Export selected', 'vs-btn vs-btn-sm', batchExport), 'Save every ticked file: to the card if one is connected, otherwise as downloads.');
    els.batch.append(el('span', 'vs-bin-hint', 'Drag a row onto a track in Arrange to place it.'), el('span', 'vs-spacer'), els.batchAll, els.batchBtn);
    els.binLib.append(els.drop, els.libTable, els.batch);
    bin.append(els.binTabs, els.binLib, els.binCard);

    // The device behaviour this page describes (on-screen load reasons, and
    // playing an oversized file from the card) arrived in firmware 1.5.
    els.fw = el('p', 'vs-fwnote', `On-screen load messages and playing large files from the card need <a href="https://tceffect.com/pages/firmware-updater" target="_blank" rel="noopener">firmware 1.5</a> or later.`);
    els.foot = el('footer', 'vs-foot vs-adv', 'Space play · I / O in and out · L loop · S split · Delete · ⌘Z undo · ⇧⌘Z redo');
    els.toasts = el('div', 'vs-toasts');
    shell.append(head, work, bin, els.fw, els.foot);
    root.append(shell, els.toasts);
  }
  function setMode(m) {
    state.mode = m === 'advanced' ? 'advanced' : 'basic';
    root.dataset.mode = state.mode;
    els.modeBasic.classList.toggle('vs-active', state.mode === 'basic'); els.modeAdv.classList.toggle('vs-active', state.mode === 'advanced');
    if (state.mode === 'basic' && state.view !== 'samples') setView('samples');
    scheduleSave();
  }
  function setHints(on) {
    state.hints = !!on;
    Tip.enable(state.hints);
    els.hintsBtn.textContent = state.hints ? 'Hints on' : 'Hints off';
    scheduleSave();
  }
  function setBin(which) {
    state.bin = which;
    els.binLib.hidden = which !== 'lib'; els.binCard.hidden = which !== 'card';
    els.binLibTab.classList.toggle('vs-active', which === 'lib'); els.binCardTab.classList.toggle('vs-active', which === 'card');
  }

  // ---- plain words for what a file is and what saving does to it ------------
  // Used by the editor's From / Saves as lines and by the toasts, so a first
  // time visitor sees that the M4A they added becomes a WAV on the way out.
  const kHz = (r) => (r / 1000).toFixed(1).replace(/\.0$/, '') + ' kHz';
  const chWord = (n) => (n === 1 ? 'mono' : n === 2 ? 'stereo' : `${n} channels`);
  const COMPRESSED = /^(MP3|M4A|AAC|OGG|OGA|OPUS|FLAC|WEBM|WMA)$/;
  // What the file is made of, the way a spec sheet would put it: bit depth,
  // channels and rate for a WAV (even one the device refuses, since the
  // reader keeps the fmt fields), channels and average bitrate for a
  // compressed file, which is all the browser's decoder reveals about it.
  function specs(s) {
    if (s.kind === 'REC' || s.kind === 'MIX') return `mono at ${kHz(RATE)}`;
    if (s.kind === 'WAV' && s.wav && s.wav.rate) {
      const w = s.wav;
      const enc = w.format === 3 ? 'float ' : (w.format === 1 || w.ok) ? '' : `format ${w.format} `;
      return `${w.bits}-bit ${enc}${chWord(w.channels)} at ${kHz(w.rate)}`;
    }
    const ch = chWord(s.srcChannels);
    if (COMPRESSED.test(s.kind) && s.blob && s.srcSeconds > 0) return `${ch} · ${Math.round(s.blob.size * 8 / s.srcSeconds / 1000)} kbps`;
    return ch;
  }
  // The same facts as specs(), phrased to sit inside a sentence rather than
  // in a row of dot-separated fields.
  function fromPhrase(s) {
    if (s.kind === 'REC') return 'your recording';
    if (s.kind === 'MIX') return 'your mix';
    if (s.kind === 'WAV' && s.wav && s.wav.rate) return `${s.wav.bits}-bit ${chWord(s.wav.channels)} WAV at ${kHz(s.wav.rate)}`;
    if (COMPRESSED.test(s.kind) && s.blob && s.srcSeconds > 0) return `${s.kind} at ${Math.round(s.blob.size * 8 / s.srcSeconds / 1000)} kbps`;
    return s.kind;
  }
  // Returns { origName, source, note }: the name the file arrived with, its
  // specs and size in one line, and one sentence naming both sides of the
  // conversion the save performs.
  function describe(s) {
    const dur = Rules.formatDuration(s.srcSeconds, true);
    const size = s.blob ? ' · ' + Rules.formatBytes(s.blob.size) : '';
    const sp = specs(s);
    const rate = (s.settings && s.settings.rate) || RATE;
    const target = `16-bit mono WAV at ${kHz(rate)}`;
    const converted = `Converted from ${fromPhrase(s)} to ${target} for VMod Pro compatibility.`;
    const origName = (s.blob && s.blob.name) || (s.kind === 'REC' ? 'your recording' : s.kind === 'MIX' ? 'your mix' : s.name);
    if (s.kind === 'REC') return { origName, source: `Recorded here · ${sp} · ${dur}${size}`, note: converted };
    if (s.kind === 'MIX') return { origName, source: `Mixed here · ${sp} · ${dur}${size}`, note: converted };
    if (s.kind === 'WAV' && s.wav && s.wav.ok) {
      const asIs = s.wav.channels === 1 && s.wav.rate === rate;
      return { origName, source: `WAV · ${sp} · ${dur}${size}`,
        note: asIs ? `Already ${target}. No conversion needed, your trim and settings are applied.` : converted };
    }
    if (s.kind === 'WAV') return { origName, source: `WAV · ${sp} · ${dur}${size}`, note: converted };
    return { origName, source: `${s.kind} · ${sp} · ${dur}${size}`, note: converted };
  }

  // ---- api handed to the views ---------------------------------------------
  const api = {
    commit, toast, refreshLibrary: renderLibrary, exportSource, describe, cardMode: () => Card.st.state === 'ready',
    project: () => state.project, sources: () => state.sources, addSource: addBufferSource,
    selectSource: (id, switchView) => { select(id); if (switchView) setView('samples'); },
    askName,
  };

  // ---- library ------------------------------------------------------------
  function uniqueName(base) {
    const taken = new Set(state.order.map((id) => state.sources.get(id).name.toLowerCase()));
    if (!taken.has(base.toLowerCase())) return base;
    for (let i = 2; i < 1000; i++) {
      const suffix = `_${i}`;
      const cand = base.slice(0, Rules.BASE_MAX - suffix.length) + suffix;
      if (!taken.has(cand.toLowerCase())) return cand;
    }
    return base;
  }
  function makeSource({ name, buffer, kind, srcChannels, srcSeconds, wav, blob }) {
    const s = {
      id: uid(), name: uniqueName(Rules.sanitizeBase(name)), kind, buffer, mip: Dsp.peaksMipmap(buffer),
      srcChannels: srcChannels || 1, srcSeconds: srcSeconds || buffer.length / RATE, wav: wav || null, blob: blob || null,
      settings: { inFrame: 0, outFrame: buffer.length, fadeInMs: 0, fadeOutMs: 0, gainDb: 0, normalize: true, rate: RATE },
      checked: false,
    };
    state.sources.set(s.id, s); state.order.push(s.id);
    return s;
  }
  async function addFiles(files) {
    const list = [...files].filter((f) => f.size > 0);
    if (!list.length) return;
    let first = null, n = 0;
    const t = toast(`Decoding ${list.length} file${list.length === 1 ? '' : 's'}…`, 'info', 0);
    for (const f of list) {
      try {
        const d = await Audio.decodeFile(f);
        const s = makeSource({ name: f.name, buffer: d.buffer, kind: d.kind, srcChannels: d.srcChannels, srcSeconds: d.srcSeconds, wav: d.wav, blob: f });
        if (!first) first = s; n++;
      } catch (e) { toast(`${f.name}: ${e.message}`, 'error', 9000); }
    }
    t.remove();
    if (first) {
      commit('add files'); renderLibrary(); select(first.id);
      if (n > 1) toast(`Added ${n} files. Each becomes a VMod Pro WAV when you save it.`, 'info', 6000);
      else toast(`Added ${first.name}. ${describe(first).note}`, 'info', 6000);
    }
  }
  function addBufferSource({ name, buffer, kind }) {
    const i16 = Dsp.toInt16(buffer, false);
    const blob = new Blob([Wav.write(i16, RATE)], { type: 'audio/wav' });
    const s = makeSource({ name, buffer, kind, blob });
    commit('add ' + kind.toLowerCase()); renderLibrary(); select(s.id); setView('samples');
    return s;
  }
  function removeSource(id) {
    const s = state.sources.get(id); if (!s) return;
    const used = state.project.clips.filter((c) => c.sourceId === id).length;
    if (used && !confirm(`${s.name} is used by ${used} clip${used === 1 ? '' : 's'} in Arrange. Remove it and those clips?`)) return;
    state.project.clips = state.project.clips.filter((c) => c.sourceId !== id);
    state.trash.set(id, s); state.sources.delete(id); state.order = state.order.filter((k) => k !== id);
    if (state.selected === id) { state.selected = state.order[0] || null; Editor.show(state.selected ? state.sources.get(state.selected) : null); }
    commit('remove'); renderLibrary(); Arrange.render();
  }
  function select(id) {
    state.selected = id;
    Editor.show(state.sources.get(id) || null);
    renderLibrary();
    scheduleSave();
  }

  function verdictFor(s) {
    if (s.kind === 'WAV' && s.wav) {
      if (s.wav.ok) {
        const notes = [];
        if (s.wav.channels === 2) notes.push('stereo, device sums to mono');
        if (s.wav.rate !== RATE) notes.push(`${(s.wav.rate / 1000).toFixed(1)} kHz, device interpolates`);
        return notes.length
          ? { level: 'warn', text: 'Plays as-is: ' + notes.join('; '), tip: 'The VMod Pro can play this file, but has to convert it every time it loads. Saving it from here gives the device exactly what it wants.' }
          : { level: 'ok', text: 'Plays as-is', tip: 'Already a WAV the VMod Pro can read. Saving still applies your trim and settings.' };
      }
      return { level: 'bad', text: s.wav.verdict, tip: 'Copied to the card as it is, this file will not load. Saving it from here fixes it.' };
    }
    if (s.kind === 'MIX') return { level: 'ok', text: 'Mixed here', tip: 'Made in Arrange. Ready to name and save.' };
    if (s.kind === 'REC') return { level: 'ok', text: 'Recorded here', tip: 'Recorded from your microphone. Ready to name and save.' };
    return { level: 'warn', text: `${s.kind}: converts on export`, tip: 'Not a WAV yet. Saving it from here turns it into one the VMod Pro can read.' };
  }

  function renderLibrary() {
    els.drop.hidden = state.order.length > 0;
    els.libTable.innerHTML = '';
    let checked = 0;
    if (state.order.length) {
      const table = el('table', 'vs-table');
      table.innerHTML = '<thead><tr><th class="vs-col-check vs-adv"></th><th>Name</th><th>Source</th><th>Device</th><th class="vs-num">Length</th><th class="vs-num">Export</th><th></th></tr></thead>';
      const tb = el('tbody');
      for (const id of state.order) {
        const s = state.sources.get(id);
        const tr = el('tr', 'vs-row-item' + (id === state.selected ? ' vs-selected' : ''));
        tr.draggable = true;
        tr.addEventListener('dragstart', (e) => { e.dataTransfer.setData('text/vs-source', id); e.dataTransfer.effectAllowed = 'copy'; });
        const cb = tip(el('input'), 'Tick to include in Export selected.'); cb.type = 'checkbox'; cb.checked = !!s.checked;
        cb.addEventListener('click', (e) => { e.stopPropagation(); s.checked = cb.checked; renderLibrary(); });
        const v = verdictFor(s);
        const frames = s.settings.outFrame - s.settings.inFrame;
        const outFrames = Math.round(frames * s.settings.rate / RATE);
        const tdCheck = el('td', 'vs-col-check vs-adv'); tdCheck.append(cb);
        const tdName = el('td', 'vs-col-name', `<span class="vs-mono">${esc(s.name)}<span class="vs-ext">${Rules.EXT}</span></span>`);
        // No duration here: the Length column already shows it.
        const tdSrc = el('td', 'vs-col-src', `${esc(s.kind)} · ${esc(specs(s))}`);
        const tdDev = el('td', 'vs-col-dev', `<span class="vs-pill vs-${v.level}" data-tip="${esc(v.tip)}">${esc(v.text)}</span>`);
        const tdLen = el('td', 'vs-num', Rules.formatDuration(frames / RATE, true));
        const tdExp = el('td', 'vs-num', `${Rules.formatBytes(Rules.bytesForFrames(outFrames))} · ${(s.settings.rate / 1000).toFixed(0)}k`);
        const tdX = el('td', 'vs-col-x');
        const x = tip(btn('✕', 'vs-btn vs-btn-xs vs-btn-ghost', (e) => { e.stopPropagation(); removeSource(id); }), 'Remove from the library. Nothing on the card is touched.');
        tdX.append(x);
        tr.append(tdCheck, tdName, tdSrc, tdDev, tdLen, tdExp, tdX);
        tr.addEventListener('click', () => { select(id); if (state.view !== 'samples') setView('samples'); });
        tb.append(tr);
        if (s.checked) checked++;
      }
      table.append(tb); els.libTable.append(table);
    }
    els.batch.hidden = state.order.length === 0;
    els.batchBtn.textContent = `Export selected (${checked})`; els.batchBtn.disabled = checked === 0;
    els.batchAll.textContent = state.order.length && state.order.every((id) => state.sources.get(id).checked) ? 'Select none' : 'Select all';
    els.binLibTab.textContent = `Library${state.order.length ? ' (' + state.order.length + ')' : ''}`;
  }
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  // ---- views ----------------------------------------------------------------
  function setView(v) {
    state.view = v;
    els.samplesView.hidden = v !== 'samples'; els.arrangeView.hidden = v !== 'arrange';
    els.tabSamples.classList.toggle('vs-active', v === 'samples'); els.tabArrange.classList.toggle('vs-active', v === 'arrange');
    if (v === 'samples') { Arrange.stop(); Editor.render(); } else { Editor.stop(); Arrange.ensureTracks(); Arrange.render(); }
    scheduleSave();
  }

  // ---- export ---------------------------------------------------------------
  async function exportSource(s, mode, opts = {}) {
    const r = await Audio.renderExport(s.buffer, s.settings);
    const name = Rules.fileName(s.name);
    if (r.bytes > Rules.POOL_BYTES && !opts.quiet) toast(`${name} is ${Rules.formatBytes(r.bytes)}, too long to hold in memory. The VMod Pro will play it straight from the card.`, 'warn', 7000);
    else if (r.bytes > Rules.WARN_BYTES && !opts.quiet) toast(`${name} uses ${Rules.formatBytes(r.bytes)}, over half of a preset's sample memory by itself.`, 'warn', 7000);
    state.lastExport = { name, wav: r.wav, rate: r.rate, frames: r.frames, bytes: r.bytes, peakDb: r.peakDb };
    if (mode === 'card' && Card.st.state === 'ready') {
      const existing = await Card.existing(name);
      if (existing && !opts.overwrite && !confirm(`${existing} is already on the card. Replace it?`)) return false;
      try { await Card.write(existing || name, r.wav); }
      catch (e) { toast(`Could not write ${name}: ${e.message}`, 'error'); return false; }
      if (!opts.quiet) toast(`Saved ${name} to the card as 16-bit mono WAV at ${kHz(r.rate)} (${Rules.formatBytes(r.bytes)}, ${Rules.formatDuration(r.seconds, true)}).`, 'ok', 6000);
      if (!opts.deferRefresh) await refreshCard();
      return true;
    }
    Card.download(name, r.wav);
    if (!opts.quiet) toast(`Downloaded ${name}, a 16-bit mono WAV at ${kHz(r.rate)}. Copy it into ${Rules.FOLDER} on the card.`, 'ok', 7000);
    return true;
  }
  async function batchExport() {
    const ids = state.order.filter((id) => state.sources.get(id).checked);
    if (!ids.length) return;
    const mode = Card.st.state === 'ready' ? 'card' : 'download';
    let overwrite = false;
    if (mode === 'card') {
      const clashes = [];
      for (const id of ids) { const ex = await Card.existing(Rules.fileName(state.sources.get(id).name)); if (ex) clashes.push(ex); }
      if (clashes.length) { if (!confirm(`${clashes.length} of these already exist on the card:\n${clashes.join(', ')}\n\nReplace them?`)) return; overwrite = true; }
    }
    let ok = 0; const t = toast(`Exporting 0 of ${ids.length}…`, 'info', 0);
    for (let i = 0; i < ids.length; i++) {
      t.textContent = `Exporting ${i + 1} of ${ids.length}…`;
      if (await exportSource(state.sources.get(ids[i]), mode, { quiet: true, overwrite, deferRefresh: true })) ok++;
      if (mode === 'download') await new Promise((r) => setTimeout(r, 400));   // browsers throttle rapid downloads
    }
    t.remove();
    toast(`Exported ${ok} of ${ids.length}${mode === 'card' ? ' to the card' : ''}.`, ok === ids.length ? 'ok' : 'warn');
    if (mode === 'card') await refreshCard();
  }

  // ---- card panel -----------------------------------------------------------
  async function refreshCard() {
    if (Card.st.state === 'ready') {
      try { state.card.items = await Card.list(); }
      catch (e) { toast('Could not read the card: ' + e.message, 'error'); Card.disconnect(); }
    } else state.card.items = [];
    renderCard(); Editor.syncControls && Editor.current && Editor.syncControls();
  }
  async function connectCard() {
    try {
      const st = await Card.connect();
      await Store.set('cardHandle', Card.st.root);
      if (st === 'ready') toast(`Connected to ${Card.st.label}.`, 'ok');
    } catch (e) { if (e.name !== 'AbortError') toast('Could not open that folder: ' + e.message, 'error'); }
    await refreshCard();
  }
  async function reconnectCard() {
    try { await Card.restore(Card.st.root, true); } catch (e) { toast(e.message, 'error'); }
    await refreshCard();
  }
  function renderCard() {
    const bar = els.cardBar, panel = els.binCard;
    bar.innerHTML = ''; panel.innerHTML = '';
    const note = (html, cls) => el('div', 'vs-card-note' + (cls ? ' ' + cls : ''), html);
    // The box spans the column; its contents sit on both edges: main thing
    // left, secondary thing right, so it reads as wide as the buttons under it.
    const row = (left, right) => {
      const L = el('span', 'vs-cardbar-l'); L.append(...left);
      const R = el('span', 'vs-cardbar-r'); R.append(...right);
      bar.append(L, R);
    };
    const items = state.card.items;
    const st = Card.supported ? Card.st.state : 'unsupported';
    bar.classList.toggle('vs-cardbar-plain', st === 'none');   // no box before a card is connected: just the button
    els.binCardTab.textContent = `Card files${st === 'ready' ? ' (' + items.filter((i) => i.listable).length + ')' : ''}`;

    if (st === 'unsupported') {
      row([el('span', 'vs-pill vs-warn', 'Download mode')], [el('span', 'vs-cardbar-text', `This browser cannot write to the card. Exports download; copy them into <code>${Rules.FOLDER}</code>.`)]);
      panel.append(note(`<b>Download mode.</b> Chrome and Edge can write to the card directly. Here, exports download and go into <code>${Rules.FOLDER}</code> on the card.`), setupSteps());
      return;
    }
    if (st === 'none') {
      bar.append(tip(btn('Connect SD Card', 'vs-btn vs-btn-card', connectCard), `Opens a folder window. Choose the SD card drive in it (its VMOD folder works too). Finished files then save straight into ${Rules.FOLDER} on the card instead of downloading.`));
      panel.append(note('No card connected.'), setupSteps());
      return;
    }
    if (st === 'stale') {
      row([tip(btn(`Reconnect ${esc(Card.st.label)}`, 'vs-btn vs-btn-sm vs-btn-primary', reconnectCard), 'The browser forgets card permission between visits. Click to allow it again.')],
        [tip(btn('Different folder', 'vs-btn vs-btn-xs vs-btn-ghost', connectCard), 'Choose a different card or folder.')]);
      panel.append(note(`Click Reconnect to read <b>${esc(Card.st.label)}</b> again. The browser asks once per visit.`));
      return;
    }
    if (st === 'unset') {
      row([el('span', 'vs-pill vs-warn', 'Not set up'), el('span', 'vs-cardbar-text', `<b>${esc(Card.st.label)}</b> has no <code>VMOD/samples</code> folder.`)],
        [tip(btn('Pick another', 'vs-btn vs-btn-xs', connectCard), 'Choose a different card or folder.')]);
      panel.append(note(`<b>${esc(Card.st.label)}</b> has not been set up by a VMod Pro. Cards over 32 GB also come formatted exFAT, which the device cannot read. Let the device do both:`, 'vs-warn'), setupSteps());
      return;
    }
    // ready
    const loadable = items.filter((i) => i.listable && i.info && i.info.ok);
    const b = Rules.budget(loadable.map((i) => i.info.bytesNeeded));
    const listable = items.filter((i) => i.listable).length;
    const budgetTip = `How much of one preset's sample memory the files on the card would use if they were all switched on at once. A preset holds ${Rules.formatBytes(Rules.POOL_BYTES)} in memory, about ${Rules.formatDuration(Rules.POOL_SECONDS_48K)} of audio. A file too long for that is not refused: the VMod Pro plays it straight from the card, up to ${Rules.MAX_STREAMS} at once.`;
    const budget = tip(el('div', 'vs-budget'), budgetTip);
    // Over the pool is amber, not red: those samples stream instead of failing.
    const fill = el('div', 'vs-budget-fill' + (b.over || b.pct > 80 ? ' vs-high' : '')); fill.style.width = Math.min(100, b.pct) + '%';
    budget.append(fill);
    row([tip(el('span', 'vs-pill vs-ok', 'Card'), 'Connected. Files you save go straight onto this card.'), el('span', 'vs-card-label', esc(Card.st.label)), budget,
      tip(el('span', 'vs-budget-text', `${Rules.formatBytes(b.used)} of ${Rules.formatBytes(Rules.POOL_BYTES)} · ${Rules.formatDuration(b.secondsAt48k)}`), budgetTip),
      tip(el('span', 'vs-card-count' + (listable > Rules.MAX_FILES ? ' vs-bad' : ''), `${listable} of ${Rules.MAX_FILES} files`), `The VMod Pro shows the first ${Rules.MAX_FILES} files in the folder. Any beyond that are invisible to it.`)],
      [tip(btn('Refresh', 'vs-btn vs-btn-xs vs-btn-ghost', refreshCard), 'Read the card again.'),
       tip(btn('Disconnect', 'vs-btn vs-btn-xs vs-btn-ghost', async () => { Card.disconnect(); await Store.set('cardHandle', null); refreshCard(); }), 'Forget this card. Finished files download instead.')]);

    if (!items.length) { panel.append(note('The samples folder is empty. Export something from the library.')); return; }
    const table = el('table', 'vs-table');
    table.innerHTML = '<thead><tr><th>File on card</th><th>Device</th><th class="vs-num">Length</th><th class="vs-num">Memory</th><th></th></tr></thead>';
    const tb = el('tbody');
    for (const it of items) {
      let level = 'ok', text = '', hint = 'The VMod Pro can play this file.';
      if (!it.listable) {
        level = 'bad';
        if (it.name.length > Rules.NAME_MAX) { text = 'Name too long: invisible to the device'; hint = `Names over ${Rules.NAME_MAX} characters including .wav do not show up on the VMod Pro. Rename it to something shorter.`; }
        else { text = 'Not .wav: invisible to the device'; hint = 'The VMod Pro only lists files whose name ends in .wav.'; }
      }
      else if (!it.info || !it.info.ok) { level = 'bad'; text = it.info ? it.info.verdict : 'Unreadable file'; hint = 'This file will not load on the VMod Pro. Add it to the library here and save it again to fix it.'; }
      else {
        const notes = [];
        if (it.info.channels === 2) notes.push('stereo, summed to mono');
        if (it.info.rate !== RATE) notes.push(`${(it.info.rate / 1000).toFixed(1)} kHz, interpolated`);
        if (notes.length) { level = 'warn'; text = notes.join('; '); hint = 'The VMod Pro plays it, but has to convert it every time it loads. Add it to the library here and save it again to fix that.'; }
        else text = 'OK';
      }
      const tr = el('tr');
      const okInfo = it.info && it.info.ok;
      tr.append(el('td', 'vs-col-name', `<span class="vs-mono">${esc(it.name)}</span>`),
        el('td', 'vs-col-dev', `<span class="vs-pill vs-${level}" data-tip="${esc(hint)}">${esc(text)}</span>`),
        el('td', 'vs-num', okInfo ? Rules.formatDuration(it.info.seconds, true) : ''),
        el('td', 'vs-num', okInfo ? Rules.formatBytes(it.info.bytesNeeded) : Rules.formatBytes(it.size)));
      const tdX = el('td', 'vs-col-x');
      const x = btn('✕', 'vs-btn vs-btn-xs vs-btn-ghost', async () => {
        if (!confirm(`Delete ${it.name} from the card?`)) return;
        try { await Card.remove(it.name); toast(`Deleted ${it.name}.`); } catch (e) { toast('Could not delete: ' + e.message, 'error'); }
        refreshCard();
      });
      tip(x, 'Delete this file from the card.'); tdX.append(x); tr.append(tdX);
      tb.append(tr);
    }
    table.append(tb);
    const wrap = el('div', 'vs-table-wrap'); wrap.append(table);
    panel.append(wrap);
    if (listable > Rules.MAX_FILES) panel.append(note(`The device lists only the first ${Rules.MAX_FILES} files in the folder; the rest are invisible to it.`, 'vs-warn'));
  }
  function setupSteps() {
    return el('ol', 'vs-steps', `<li>Put the card in the VMod Pro and power on.</li><li>Settings › SD Card › <b>Format</b>. It works even when the card will not mount, formats FAT32 at any size, and creates the folders.</li><li>Put the card back in the computer and connect it here.</li>`);
  }

  // ---- recording -------------------------------------------------------------
  async function toggleRecord() {
    if (state.recording) {
      const rec = state.recording; state.recording = null;
      els.recBtn.textContent = 'Record'; els.recBtn.classList.remove('vs-recording');
      const buf = await rec.stop();
      if (buf.length < RATE * 0.05) { toast('Nothing recorded.'); return; }
      let n = 1; while (state.order.some((id) => state.sources.get(id).name === `REC_${n}`)) n++;
      addBufferSource({ name: `REC_${n}`, buffer: buf, kind: 'REC' });
      return;
    }
    try {
      const rec = await Audio.startRecording((lvl) => { els.recBtn.style.setProperty('--vs-level', Math.min(1, lvl).toFixed(2)); });
      state.recording = rec; els.recBtn.textContent = 'Stop recording'; els.recBtn.classList.add('vs-recording');
      toast('Recording. Click again to stop.');
    } catch (e) { toast('Microphone unavailable: ' + e.message, 'error'); }
  }

  // ---- undo -------------------------------------------------------------------
  function snapshot() {
    return JSON.stringify({
      order: state.order, selected: state.selected,
      sources: state.order.map((id) => { const s = state.sources.get(id); return { id, name: s.name, settings: s.settings }; }),
      project: state.project,
    });
  }
  let current = null;
  function commit(label) {
    const snap = snapshot();
    if (current !== null && snap !== current) { state.undo.push(current); if (state.undo.length > 100) state.undo.shift(); state.redo.length = 0; }
    current = snap;
    scheduleSave();
  }
  function applySnapshot(json) {
    const d = JSON.parse(json);
    for (const rec of d.sources) {
      let s = state.sources.get(rec.id);
      if (!s) { s = state.trash.get(rec.id); if (!s) continue; state.sources.set(rec.id, s); state.trash.delete(rec.id); }
      s.name = rec.name; s.settings = rec.settings;
    }
    for (const id of [...state.sources.keys()]) if (!d.order.includes(id)) { state.trash.set(id, state.sources.get(id)); state.sources.delete(id); }
    state.order = d.order.filter((id) => state.sources.has(id));
    state.project = d.project;
    state.selected = state.sources.has(d.selected) ? d.selected : (state.order[0] || null);
    current = json;
    renderLibrary(); Editor.show(state.selected ? state.sources.get(state.selected) : null); Arrange.render();
    scheduleSave();
  }
  function undo() { if (!state.undo.length) return; state.redo.push(current); applySnapshot(state.undo.pop()); toast('Undo'); }
  function redo() { if (!state.redo.length) return; state.undo.push(current); applySnapshot(state.redo.pop()); toast('Redo'); }

  // ---- persistence -------------------------------------------------------------
  function scheduleSave() { clearTimeout(saveTimer); saveTimer = setTimeout(save, 600); }
  async function save() {
    for (const id of state.order) {
      const s = state.sources.get(id);
      const sig = JSON.stringify([s.name, s.settings]);
      if (savedSig.get(id) === sig) continue;
      savedSig.set(id, sig);
      await Store.putSource({ id, name: s.name, kind: s.kind, settings: s.settings, blob: s.blob, srcChannels: s.srcChannels, addedAt: Date.now() });
    }
    for (const id of [...savedSig.keys()]) if (!state.sources.has(id)) { savedSig.delete(id); await Store.delSource(id); }
    await Store.set('project', state.project);
    await Store.set('ui', { order: state.order, selected: state.selected, view: state.view, mode: state.mode, hints: state.hints });
  }
  async function restore() {
    const recs = await Store.allSources();
    const ui = (await Store.get('ui')) || {};
    const t = recs.length ? toast(`Restoring ${recs.length} file${recs.length === 1 ? '' : 's'}…`, 'info', 0) : null;
    const byId = new Map(recs.map((r) => [r.id, r]));
    const order = (ui.order || []).filter((id) => byId.has(id)).concat(recs.map((r) => r.id).filter((id) => !(ui.order || []).includes(id)));
    for (const id of order) {
      const r = byId.get(id);
      try {
        const d = await Audio.decodeFile(r.blob);
        const s = { id: r.id, name: r.name, kind: r.kind, buffer: d.buffer, mip: Dsp.peaksMipmap(d.buffer), srcChannels: r.srcChannels || d.srcChannels,
          srcSeconds: d.srcSeconds, wav: d.wav, blob: r.blob, settings: r.settings, checked: false };
        s.settings.outFrame = Math.min(s.settings.outFrame, d.buffer.length);
        state.sources.set(id, s); state.order.push(id); savedSig.set(id, JSON.stringify([s.name, s.settings]));
      } catch (e) { await Store.delSource(id); }
    }
    if (t) t.remove();
    const p = await Store.get('project');
    if (p && p.tracks) { p.clips = (p.clips || []).filter((c) => state.sources.has(c.sourceId)); state.project = p; }
    state.selected = state.sources.has(ui.selected) ? ui.selected : (state.order[0] || null);
    if (ui.view === 'arrange' || ui.view === 'samples') state.view = ui.view;
    if (ui.mode === 'advanced') state.mode = 'advanced';
    if (ui.hints === false) state.hints = false;
    current = snapshot();
    const h = await Store.get('cardHandle');
    if (h && Card.supported) { try { await Card.restore(h, false); } catch (e) { /* stale */ } await refreshCard(); }
  }

  // ---- global input --------------------------------------------------------------
  function bindGlobal() {
    document.addEventListener('keydown', (e) => {
      const t = e.target;
      const typing = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable);
      if ((e.metaKey || e.ctrlKey) && (e.key === 'z' || e.key === 'Z')) { if (typing) return; e.preventDefault(); if (e.shiftKey) redo(); else undo(); return; }
      if ((e.metaKey || e.ctrlKey) && e.key === 'y') { if (typing) return; e.preventDefault(); redo(); return; }
      if (typing && e.key !== 'Escape') return;
      if (!root.contains(document.activeElement) && document.activeElement !== document.body) return;
      const used = state.view === 'samples' ? Editor.key(e) : Arrange.key(e);
      if (used) e.preventDefault();
    });
    // File drops anywhere on the studio (the arranger handles its own source drops)
    let over = 0;
    root.addEventListener('dragenter', (e) => { if ([...e.dataTransfer.types].includes('Files')) { over++; root.classList.add('vs-dragover'); } });
    root.addEventListener('dragleave', () => { if (--over <= 0) { over = 0; root.classList.remove('vs-dragover'); } });
    root.addEventListener('dragover', (e) => { if ([...e.dataTransfer.types].includes('Files')) e.preventDefault(); });
    root.addEventListener('drop', (e) => { over = 0; root.classList.remove('vs-dragover'); if (e.dataTransfer.files && e.dataTransfer.files.length) { e.preventDefault(); addFiles(e.dataTransfer.files); } });
  }

  // ---- toasts + prompts ------------------------------------------------------------
  function toast(msg, level = 'info', ms = 4000) {
    const t = el('div', 'vs-toast vs-toast-' + level, esc(msg));
    els.toasts.append(t);
    if (ms) setTimeout(() => t.remove(), ms);
    return t;
  }
  function askName(title, suggestion) {
    return new Promise((resolve) => {
      const back = el('div', 'vs-modal-back');
      const box = el('div', 'vs-modal');
      const inp = el('input', 'vs-name'); inp.value = uniqueName(Rules.sanitizeBase(suggestion)); inp.spellcheck = false; inp.maxLength = Rules.BASE_MAX + 4;
      const msg = el('div', 'vs-name-msg', '');
      const upd = () => { const st = Rules.nameStatus(inp.value); msg.textContent = st.message; msg.className = 'vs-name-msg vs-' + st.level; ok.disabled = st.level === 'bad'; };
      const ok = btn('OK', 'vs-btn vs-btn-primary', () => { const v = inp.value; back.remove(); resolve(uniqueName(v)); });
      const cancel = btn('Cancel', 'vs-btn', () => { back.remove(); resolve(null); });
      inp.addEventListener('input', upd);
      inp.addEventListener('keydown', (e) => { e.stopPropagation(); if (e.key === 'Enter' && !ok.disabled) ok.click(); if (e.key === 'Escape') cancel.click(); });
      const row = el('div', 'vs-row'); row.append(el('span', 'vs-spacer'), cancel, ok);
      box.append(el('h3', '', esc(title)), inp, msg, row);
      back.append(box); root.append(back); inp.focus(); inp.select(); upd();
    });
  }

  return { boot, state, addFiles, exportSource, commit, undo, redo, setView, setMode, setHints, select, refreshCard, api, views: { Editor, Arrange }, modules: { Rules, Dsp, Wav, Audio, Card, Store, Tip, WaveView } };
})();

window.VmodStudio = App;
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', App.boot); else App.boot();
})();
